<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Order Management' : 'Deleted Orders'"
              :subtitle="isDeletePage() != true ? 'Track and manage all customer orders' : 'Restore or permanently delete orders'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      v-if="!isSeller"
                      color="primary"
                      elevation="1"
                      small
                      to="/orders/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Order
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Orders</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <!-- First Row: Search and Main Filters -->
              <v-row class="filter-row" dense>
                <v-col cols="12" :md="isSeller ? 6 : 4">
                  <v-text-field
                    v-model="form.search_keyword"
                    label="Search orders..."
                    placeholder="Search by order number, address, or customer"
                    outlined
                    dense
                    clearable
                    hide-details="auto"
                    prepend-inner-icon="mdi-magnify"
                    @input="debouncedSearch"
                    @click:clear="clearSearch"
                    class="filter-field"
                  >
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="2" v-if="!isSeller">
                  <v-autocomplete
                    v-model="form.seller_id"
                    :items="sellers"
                    label="Seller Filter"
                    placeholder="All Sellers"
                    outlined
                    dense
                    clearable
                    hide-details="auto"
                    prepend-inner-icon="mdi-account-tie"
                    @change="getData"
                    @click:clear="clearSellerFilter"
                    class="filter-field"
                  >
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" md="2" v-if="!isSeller">
                  <v-autocomplete
                    v-model="form.patient_id"
                    :items="patients"
                    label="Customer"
                    placeholder="All Customers"
                    outlined
                    dense
                    clearable
                    hide-details="auto"
                    prepend-inner-icon="mdi-account"
                    @change="getData"
                    @click:clear="clearPatientFilter"
                    class="filter-field"
                  >
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" :md="isSeller ? 4 : 2">
                  <v-autocomplete
                    v-model="form.payment_status"
                    :items="paymentStatusItems"
                    label="Payment Status"
                    placeholder="All Payments"
                    outlined
                    dense
                    clearable
                    hide-details="auto"
                    prepend-inner-icon="mdi-credit-card"
                    @change="getData"
                    @click:clear="clearPaymentStatusFilter"
                    class="filter-field"
                  >
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" md="2">
                  <div class="d-flex align-center justify-end h-100">
                    <v-btn
                      v-if="hasActiveFilters"
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                    >
                      <v-icon left x-small>mdi-filter-off</v-icon>
                      Clear All
                    </v-btn>
                  </div>
                </v-col>
              </v-row>

              <!-- Second Row: Additional Filters -->
              <v-row class="filter-row mt-2" dense>
                <v-col cols="12" :md="isSeller ? 6 : 3">
                  <v-autocomplete
                    v-model="form.order_status"
                    :items="orderStatusItems"
                    label="Order Status"
                    placeholder="All Statuses"
                    outlined
                    dense
                    clearable
                    hide-details="auto"
                    prepend-inner-icon="mdi-package-variant"
                    @change="() => { form.order_status_in = null; getData(); }"
                    @click:clear="clearOrderStatusFilter"
                    class="filter-field"
                  >
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" md="3" v-if="!isSeller">
                  <div class="ht-toggle-card" :class="{ 'ht-toggle-card--on': !!form.has_group_multi }">
                    <span class="ht-toggle-card__icon">
                      <v-icon small>mdi-account-multiple-outline</v-icon>
                    </span>
                    <v-switch
                      v-model="form.has_group_multi"
                      inset
                      dense
                      hide-details
                      class="ht-toggle-card__switch"
                      title="Show only orders that contain items from more than one seller"
                      @change="() => { page = 1; getData(); }"
                    >
                      <template v-slot:label>
                        <span class="ht-toggle-card__label">Multi-seller orders</span>
                      </template>
                    </v-switch>
                  </div>
                </v-col>

                <v-col cols="12" :md="isSeller ? 6 : 3">
                  <div class="d-flex align-center justify-end h-100">
                    <span class="text-caption grey--text mr-3">
                      {{ orders.length }} of {{ count }} orders shown
                    </span>
                  </div>
                </v-col>
              </v-row>

              <!-- Third Row: Date Filters -->
              <v-row class="filter-row mt-2" dense>
                <v-col cols="12" md="auto">
                  <div class="d-flex align-center flex-wrap gap-1" style="gap:6px">
                    <span class="text-caption grey--text mr-1">Date:</span>
                    <v-chip
                      small
                      :color="form.date_preset === 'today' ? 'primary' : ''"
                      :outlined="form.date_preset !== 'today'"
                      @click="setDatePreset('today')"
                    >Today</v-chip>
                    <v-chip
                      small
                      :color="form.date_preset === 'yesterday' ? 'primary' : ''"
                      :outlined="form.date_preset !== 'yesterday'"
                      @click="setDatePreset('yesterday')"
                    >Yesterday</v-chip>
                    <v-chip
                      small
                      :color="form.date_preset === 'this_week' ? 'primary' : ''"
                      :outlined="form.date_preset !== 'this_week'"
                      @click="setDatePreset('this_week')"
                    >This Week</v-chip>
                    <v-chip
                      small
                      :color="form.date_preset === 'this_month' ? 'primary' : ''"
                      :outlined="form.date_preset !== 'this_month'"
                      @click="setDatePreset('this_month')"
                    >This Month</v-chip>
                    <v-chip
                      small
                      :color="form.date_preset === 'custom' ? 'primary' : ''"
                      :outlined="form.date_preset !== 'custom'"
                      @click="form.date_preset = 'custom'"
                    >Custom</v-chip>
                    <v-chip
                      v-if="form.date_preset"
                      small color="error" outlined
                      @click="clearDateFilter"
                    ><v-icon x-small left>mdi-close</v-icon>Clear</v-chip>
                  </div>
                </v-col>

                <v-col cols="12" md="5" v-if="form.date_preset === 'custom'">
                  <div class="d-flex align-center" style="gap:8px">
                    <!-- From date picker -->
                    <v-menu
                      v-model="filterFromMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.start_date"
                          label="From"
                          prepend-inner-icon="mdi-calendar"
                          readonly outlined dense hide-details="auto"
                          clearable
                          style="max-width:175px"
                          v-bind="attrs" v-on="on"
                          @click:clear="form.start_date = ''; form.end_date = '';"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.start_date"
                        :max="form.end_date || undefined"
                        no-title scrollable
                        @input="filterFromMenu = false"
                      ></v-date-picker>
                    </v-menu>

                    <span class="text-caption grey--text">to</span>

                    <!-- To date picker — only enabled after From is picked -->
                    <v-menu
                      v-model="filterToMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y min-width="auto"
                      :disabled="!form.start_date"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.end_date"
                          label="To"
                          prepend-inner-icon="mdi-calendar"
                          readonly outlined dense hide-details="auto"
                          clearable
                          style="max-width:175px"
                          :disabled="!form.start_date"
                          v-bind="attrs" v-on="on"
                          @click:clear="form.end_date = '';"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.end_date"
                        :min="form.start_date || undefined"
                        no-title scrollable
                        @input="filterToMenu = false; getData()"
                      ></v-date-picker>
                    </v-menu>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Orders Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Orders List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-receipt</v-icon>
                      {{ count }} {{ count === 1 ? "Order" : "Orders" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage() && orders.length > 0 && !isSeller"
                      x-small
                      color="success"
                      outlined
                      @click="generateExcel"
                      class="px-2 mr-2"
                      :loading="isExcelloading"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export All
                    </v-btn>

                    <!-- Seller Excel Export Button -->
                    <v-btn
                      v-if="!isDeletePage() && orders.length > 0"
                      x-small
                      color="orange"
                      outlined
                      @click="openSellerDateFilterDialog"
                      class="px-2 mr-2"
                      :loading="isSellerExporting"
                    >
                      <v-icon left x-small>mdi-store-outline</v-icon>
                      Seller Export
                    </v-btn>

                    <!-- Doctor Payout Export Button (admin only) -->
                    <v-btn
                      v-if="!isDeletePage() && orders.length > 0 && !isSeller"
                      x-small
                      color="teal"
                      outlined
                      @click="openDoctorPayoutDialog"
                      class="px-2"
                      :loading="isDoctorExporting"
                    >
                      <v-icon left x-small>mdi-doctor</v-icon>
                      Doctor Payout
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading orders..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="groupedOrders"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                show-expand
                :expanded.sync="expandedGroupRows"
                item-key="_rowKey"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Sr No Column -->
                <template v-slot:[`item.sr_no`]="{ index }">
                  <div class="text-center font-weight-medium">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </div>
                </template>

                <!-- Order Info Column -->
                <template v-slot:[`item.order_info`]="{ item }">
                  <div v-if="item._isGroup" class="d-flex align-center py-2" style="cursor:pointer" @click="openGroupOrderDialog(item)">
                    <v-avatar size="36" class="mr-3" color="deep-purple">
                      <v-icon color="white">mdi-package-variant-closed-plus</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium text-body-2 deep-purple--text">
                        {{ item.order_number || "N/A" }}
                      </div>
                      <div class="text-caption grey--text">{{ item.seller_count }} package{{ item.seller_count === 1 ? '' : 's' }}</div>
                    </div>
                  </div>
                  <div v-else class="d-flex align-center py-2" style="cursor:pointer" @click="navigateToOrderItems(item.id)">
                    <v-avatar size="36" class="mr-3" color="primary">
                      <v-icon color="white">mdi-receipt</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium text-body-2 primary--text text-decoration-underline">
                        {{ item.order_number || "N/A" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Customer Info Column -->
                <template v-slot:[`item.customer_info`]="{ item }">
                  <div v-if="item.customer">
                    <div class="text-body-2 font-weight-medium">
                      <v-icon x-small class="mr-1">mdi-account-heart</v-icon>
                      {{
                        item.customer.first_name +
                        " " +
                        (item.customer.last_name || "")
                      }}
                    </div>
                    <div class="text-caption grey--text mt-1">
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>
                      {{ item.customer.phone || "No phone" }}
                    </div>
                    <div class="text-caption grey--text" v-if="item.customer.city || item.customer.state">
                      <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                      {{ [item.customer.city, item.customer.state].filter(Boolean).join(", ") }}
                    </div>
                  </div>
                  <div v-else class="text-caption grey--text">
                    No customer info
                  </div>
                </template>

                <!-- Seller Info Column -->
                <template v-slot:[`item.seller_info`]="{ item }">
                  <div v-if="item._isGroup" style="cursor:pointer" @click="openGroupOrderDialog(item)">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <div class="text-body-2 font-weight-medium deep-purple--text" v-bind="attrs" v-on="on">
                          <v-icon x-small class="mr-1">mdi-account-group</v-icon>
                          {{ item.seller_count }} Sellers
                        </div>
                      </template>
                      <div>
                        <div v-for="sib in item.siblings" :key="sib.id">
                          {{ sib.seller ? (sib.seller.store_name || (sib.seller.first_name + " " + (sib.seller.last_name || ""))) : "Unknown seller" }}
                        </div>
                      </div>
                    </v-tooltip>
                  </div>
                  <div v-else-if="item.seller">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <div
                          class="text-body-2 font-weight-bold text-decoration-underline"
                          style="cursor:pointer; display:inline-block"
                          v-bind="attrs"
                          v-on="on"
                          @click="$router.push(`/sellers/${encryptIt(item.seller.id)}`)"
                        >
                          <v-icon x-small class="mr-1">mdi-store</v-icon>
                          {{ item.seller.store_name || (item.seller.first_name + " " + (item.seller.last_name || "")) }}
                        </div>
                      </template>
                      <div>
                        <div class="font-weight-medium">{{ item.seller.first_name + " " + (item.seller.last_name || "") }}</div>
                        <div v-if="item.seller.phone">{{ item.seller.phone }}</div>
                        <div v-if="item.seller.city">{{ item.seller.city }}</div>
                      </div>
                    </v-tooltip>
                  </div>
                  <div v-else class="text-caption grey--text">
                    No seller info
                  </div>
                </template>


                <!-- Financial Info Column -->
                <template v-slot:[`item.financial_info`]="{ item }">
                  <div v-if="item._isGroup">
                    <div class="text-body-2 font-weight-bold text-success">
                      ₹{{ groupTotal(item).toFixed(2) }}
                    </div>
                    <div class="text-caption grey--text">
                      Items: {{ groupItemCount(item) }}
                    </div>
                  </div>
                  <div v-else>
                    <div class="text-body-2 font-weight-bold text-success">
                      ₹{{ getOrderTotal(item).toFixed(2) }}
                    </div>
                    <div class="text-caption grey--text">
                      Items: {{ item.order_items ? item.order_items.length : 0 }}
                    </div>
                    <div
                      class="text-caption grey--text"
                      v-if="parseFloat(item.delivery_charges || 0) > 0"
                    >
                      Delivery: ₹{{ parseFloat(item.delivery_charges || 0).toFixed(2) }}
                    </div>
                  </div>
                </template>

                <!-- Payment Type Column -->
                <template v-slot:[`item.payment_status`]="{ item }">
                  <v-chip
                    :color="getPaymentTypeColor(item._isGroup ? item.siblings[0].payment_type : item.payment_type)"
                    :text-color="'white'"
                    x-small
                  >
                    <v-icon left x-small>
                      {{ getPaymentTypeIcon(item._isGroup ? item.siblings[0].payment_type : item.payment_type) }}
                    </v-icon>
                    {{ getPaymentTypeText(item._isGroup ? item.siblings[0].payment_type : item.payment_type) }}
                  </v-chip>
                </template>

                <!-- Order Status Column -->
                <template v-slot:[`item.order_status`]="{ item }">
                  <div
                    v-if="item._isGroup && isGroupStatusMixed(item)"
                    class="d-flex flex-wrap"
                    style="gap: 4px"
                  >
                    <v-chip
                      v-for="(st, idx) in getGroupStatusList(item)"
                      :key="idx"
                      :color="st.color"
                      text-color="white"
                      x-small
                    >
                      <v-icon left x-small>{{ st.icon }}</v-icon>
                      {{ st.text }}
                    </v-chip>
                  </div>
                  <v-chip
                    v-else
                    :color="(item._isGroup ? item.siblings[0] : item).cancelled_at && (item._isGroup ? item.siblings[0] : item).order_status === 7 ? 'red darken-1' : getOrderStatusColor((item._isGroup ? item.siblings[0] : item).order_status)"
                    :text-color="'white'"
                    x-small
                  >
                    <v-icon left x-small>
                      {{ (item._isGroup ? item.siblings[0] : item).cancelled_at && (item._isGroup ? item.siblings[0] : item).order_status === 7 ? 'mdi-close-circle' : getOrderStatusIcon((item._isGroup ? item.siblings[0] : item).order_status) }}
                    </v-icon>
                    {{ (item._isGroup ? item.siblings[0] : item).cancelled_at && (item._isGroup ? item.siblings[0] : item).order_status === 7 ? 'Cancelled' : getOrderStatusText((item._isGroup ? item.siblings[0] : item).order_status) }}
                  </v-chip>
                </template>


                <!-- Delivery Info Column -->
                <template v-slot:[`item.delivery_info`]="{ item }">
                  <span v-if="item._isGroup" class="text-caption grey--text">
                    {{ item.siblings.length }} shipments &middot; expand to view
                  </span>
                  <div v-else-if="item.delivery" style="min-width:120px">
                    <v-chip
                      x-small
                      :color="getDeliveryStatusColor(item.delivery.status)"
                      dark
                      style="height:18px;font-size:10px;"
                    >
                      {{ item.delivery.status || 'pending' }}
                    </v-chip>
                    <div
                      v-if="item.delivery.awb_number"
                      class="text-caption grey--text mt-1"
                      style="font-size:10px;line-height:1.2;word-break:break-all;"
                    >
                      {{ item.delivery.awb_number }}
                    </div>
                    <div
                      v-if="item.delivery.courier_name"
                      class="text-caption grey--text"
                      style="font-size:10px;line-height:1.2;"
                    >
                      {{ item.delivery.courier_name }}
                    </div>
                  </div>
                  <span v-else class="text-caption grey--text">—</span>
                </template>

                <!-- Created Date Column -->
                <template v-slot:[`item.created_at`]="{ item }">
                  <div class="text-body-2">
                    {{ formatDate(item.created_at) }}
                  </div>
                </template>

                <!-- Actions Column -->
                <template v-slot:[`item.actions`]="{ item }">
                  <div v-if="item._isGroup" class="d-flex justify-center flex-wrap" style="gap: 6px">
                    <v-btn x-small outlined color="deep-purple" @click="openGroupOrderDialog(item)">
                      <v-icon x-small left>mdi-eye</v-icon>
                      View Packets
                    </v-btn>
                    <v-tooltip v-if="!isSeller" bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          x-small
                          outlined
                          color="blue darken-1"
                          @click="downloadCustomerInvoice(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon x-small left>mdi-file-document-outline</v-icon>
                          Invoice
                        </v-btn>
                      </template>
                      <span>Download Customer Invoice (all sellers, one PDF)</span>
                    </v-tooltip>
                  </div>
                  <div v-else class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <div class="action-btn-group">
                        <!-- Ship Order / Track Progress Button -->
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <v-btn
                              v-if="isOrderBooked(item) || item.order_status === 11 || (item.order_status === 7 && item.cancelled_at)"
                              small
                              text
                              color="green darken-1"
                              @click="showTrackingProgress(item)"
                              class="action-btn-group__btn"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small left>mdi-truck-delivery</v-icon>
                              Track
                            </v-btn>
                            <v-btn
                              v-else
                              small
                              text
                              color="teal darken-1"
                              @click="openShipConfirm(item)"
                              class="action-btn-group__btn"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small left>mdi-truck-fast</v-icon>
                              Ship
                            </v-btn>
                          </template>
                          <span>{{ isOrderBooked(item) || item.order_status === 11 ? 'Track Progress' : 'Ship Order' }}</span>
                        </v-tooltip>

                        <!-- View/Edit Button (hidden for seller) -->
                        <v-tooltip v-if="!isSeller" bottom>
                          <template #activator="{ on, attrs }">
                            <v-btn
                              small
                              text
                              :color="item.order_status === 0 ? 'primary' : 'grey darken-1'"
                              @click="navigateToOrder(item.id)"
                              class="action-btn-group__btn"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small>{{ item.order_status === 0 ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ item.order_status === 0 ? 'Edit Order' : 'View Order (read-only)' }}</span>
                        </v-tooltip>

                        <!-- Actions Menu -->
                        <v-menu bottom left>
                          <template #activator="{ on, attrs }">
                            <v-btn small text color="grey darken-2" class="action-btn-group__btn" v-bind="attrs" v-on="on" title="More actions">
                              <v-icon small>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>

                        <v-list dense>
                          <v-list-item
                            :to="`/orders/${encryptIt(item.id)}/items`"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="info"
                                >mdi-format-list-bulleted</v-icon
                              >
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption"
                                >Order Items</v-list-item-title
                              >
                            </v-list-item-content>
                          </v-list-item>

                          <v-list-item
                            v-if="item.order_status !== 11 && !(item.order_status === 7 && item.cancelled_at)"
                            @click="generateInvoice(item.id)"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="blue"
                                >mdi-download</v-icon
                              >
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption"
                                >Download Invoice</v-list-item-title
                              >
                            </v-list-item-content>
                          </v-list-item>

                          <v-list-item
                            v-if="item.label_pdf_url && item.order_status < 6"
                            @click="printLabel(item.id)"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="teal"
                                >mdi-printer</v-icon
                              >
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption"
                                >Print Label</v-list-item-title
                              >
                            </v-list-item-content>
                          </v-list-item>

                          <v-list-item
                            v-if="!isSeller && item.order_group && item.order_group.seller_count > 1"
                            @click="downloadAllGroupDocuments(item)"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="deep-purple"
                                >mdi-package-variant-closed-plus</v-icon
                              >
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption"
                                >Download All Packages' Invoices</v-list-item-title
                              >
                            </v-list-item-content>
                          </v-list-item>

                          <v-list-item
                            v-if="!isSeller"
                            @click="openStatusUpdate(item)"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="orange">mdi-update</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">Update Status</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>

                          <v-list-item
                            v-if="item.delivery && ['booked', 'manifested', 'confirmed'].includes((item.delivery.status || '').toLowerCase())"
                            @click="openCancelShipmentConfirm(item)"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="red">mdi-cancel</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption red--text">Cancel Shipment</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>

                          <v-list-item
                            v-if="isSeller && item.order_status < 3 && item.order_status !== 11"
                            @click="openRejectDialog(item)"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="red">mdi-close-circle</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption red--text">Reject Order</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>

                          <v-divider></v-divider>

                          <v-list-item
                            v-if="!isSeller"
                            @click="showDeleteConfirmation(item)"
                            class="error--text"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="red">mdi-delete</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption"
                                >Delete Order</v-list-item-title
                              >
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                        </v-menu>
                      </div>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="Restore_this(item.id)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Order</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Custom expand icon: group rows open the sellers popup instead of expanding inline -->
                <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
                  <v-icon
                    v-if="item._isGroup"
                    small
                    color="deep-purple"
                    style="cursor:pointer"
                    @click="openGroupOrderDialog(item)"
                  >
                    mdi-account-group
                  </v-icon>
                  <v-icon
                    v-else
                    class="v-data-table__expand-icon"
                    :class="{ 'v-data-table__expand-icon--active': isExpanded }"
                    @click="expand(!isExpanded)"
                  >
                    $expand
                  </v-icon>
                </template>

                <!-- Expanded Row: single-order item preview (group rows use the sellers popup instead) -->
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="pa-0">
                    <div class="expanded-row-content pa-3">
                      <v-simple-table dense class="items-mini-table">
                        <template #default>
                          <thead>
                            <tr>
                              <th class="text-caption">Product</th>
                              <th class="text-caption text-center">Qty</th>
                              <th class="text-caption text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="oi in item.order_items" :key="oi.id">
                              <td class="text-caption">{{ getItemName(oi) }}</td>
                              <td class="text-caption text-center">{{ oi.quantity }}</td>
                              <td class="text-caption text-right">₹{{ parseFloat(oi.final_amount || 0).toFixed(2) }}</td>
                            </tr>
                            <tr v-if="!item.order_items || item.order_items.length === 0">
                              <td colspan="3" class="text-caption grey--text text-center">No items</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </div>
                  </td>
                </template>

                <!-- Loading Slot -->
                <template v-slot:loading>
                  <div class="text-center pa-4">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="32"
                    ></v-progress-circular>
                    <div class="mt-2 text-caption grey--text">
                      Loading orders...
                    </div>
                  </div>
                </template>

                <!-- No Data Slot -->
                <template v-slot:no-data>
                  <empty-state icon="mdi-receipt-outline" :title="isDeletePage() ? &quot;No deleted orders found&quot; : &quot;No orders found&quot;" :message="isDeletePage() ? &quot;All orders are currently active&quot; : &quot;Try adjusting your search criteria or create a new order&quot;">
                    <v-btn
                    v-if="hasActiveFilters"
                    color="primary"
                    outlined
                    class="mt-3"
                    x-small
                    @click="clearAllFilters"
                    >
                    <v-icon left x-small>mdi-filter-off</v-icon>
                    Clear Filters
                    </v-btn>
                  </empty-state>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Success Dialog -->
      <v-dialog v-model="is_deleted_order" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Order deleted successfully.</h3>
                      <br />
                      <v-btn color="success" @click="is_deleted_order = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Restore Success Dialog -->
      <v-dialog v-model="is_restored_order" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Order restored successfully.</h3>
                      <br />
                      <v-btn color="success" @click="is_restored_order = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Sellers Details Dialog (multi-seller order breakdown) -->
      <v-dialog v-model="showGroupOrderDialog" max-width="900" scrollable>
        <v-card v-if="selectedGroupOrder">
          <v-card-title class="d-flex align-center justify-space-between py-3 px-4">
            <div>
              <div class="text-subtitle-1 font-weight-bold deep-purple--text">
                {{ selectedGroupOrder.order_number || "N/A" }}
              </div>
              <div class="text-caption grey--text d-flex align-center flex-wrap">
                <span>{{ selectedGroupOrder.seller_count }} sellers &middot; ₹{{ groupTotal(selectedGroupOrder).toFixed(2) }}</span>
                <v-chip
                  v-if="selectedGroupOrder.siblings[0]"
                  :color="getPaymentTypeColor(selectedGroupOrder.siblings[0].payment_type)"
                  text-color="white"
                  x-small
                  class="ml-2"
                >
                  <v-icon left x-small>{{ getPaymentTypeIcon(selectedGroupOrder.siblings[0].payment_type) }}</v-icon>
                  {{ getPaymentTypeText(selectedGroupOrder.siblings[0].payment_type) }}
                </v-chip>
              </div>
            </div>
            <v-btn icon @click="closeGroupOrderDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-3" style="max-height: 70vh">
            <div
              v-for="(sib, idx) in selectedGroupOrder.siblings"
              :key="sib.id"
              class="seller-sub-block mb-3 pa-3"
            >
              <div class="d-flex flex-wrap align-center justify-space-between mb-2">
                <div class="d-flex align-center">
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-chip x-small outlined color="deep-purple" class="mr-2" v-bind="attrs" v-on="on">
                        <v-icon x-small left>mdi-package-variant</v-icon>
                        Package {{ idx + 1 }}
                      </v-chip>
                    </template>
                    <div>
                      <template v-if="sib.delivery && (sib.delivery.package_weight || sib.delivery.package_length || sib.delivery.package_breadth || sib.delivery.package_height)">
                        <div v-if="sib.delivery.package_weight">Weight: {{ sib.delivery.package_weight }} g</div>
                        <div v-if="sib.delivery.package_length || sib.delivery.package_breadth || sib.delivery.package_height">
                          Dimensions (L×B×H): {{ sib.delivery.package_length || '-' }} × {{ sib.delivery.package_breadth || '-' }} × {{ sib.delivery.package_height || '-' }} cm
                        </div>
                      </template>
                      <div v-else>Dimensions & weight not available yet</div>
                    </div>
                  </v-tooltip>

                  <v-tooltip v-if="sib.seller" bottom>
                    <template #activator="{ on, attrs }">
                      <div
                        class="text-body-2 font-weight-bold text-decoration-underline"
                        style="cursor:pointer; display:inline-block"
                        v-bind="attrs"
                        v-on="on"
                        @click="$router.push(`/sellers/${encryptIt(sib.seller.id)}`)"
                      >
                        <v-icon x-small class="mr-1">mdi-store</v-icon>
                        {{ sib.seller.store_name || (sib.seller.first_name + " " + (sib.seller.last_name || "")) }}
                      </div>
                    </template>
                    <div>
                      <div class="font-weight-medium">{{ sib.seller.first_name + " " + (sib.seller.last_name || "") }}</div>
                      <div v-if="sib.seller.phone">{{ sib.seller.phone }}</div>
                      <div v-if="sib.seller.city">{{ sib.seller.city }}</div>
                    </div>
                  </v-tooltip>
                </div>

                <div class="d-flex align-center flex-wrap">
                  <v-chip
                    :color="sib.cancelled_at && sib.order_status === 7 ? 'red darken-1' : getOrderStatusColor(sib.order_status)"
                    text-color="white"
                    x-small
                    class="mr-2 mb-1"
                  >
                    <v-icon left x-small>{{ sib.cancelled_at && sib.order_status === 7 ? 'mdi-close-circle' : getOrderStatusIcon(sib.order_status) }}</v-icon>
                    {{ sib.cancelled_at && sib.order_status === 7 ? 'Cancelled' : getOrderStatusText(sib.order_status) }}
                  </v-chip>
                  <span class="text-body-2 font-weight-bold text-success mr-3">₹{{ getOrderTotal(sib).toFixed(2) }}</span>

                  <div class="action-btn-group mr-2">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          v-if="isOrderBooked(sib) || sib.order_status === 11 || (sib.order_status === 7 && sib.cancelled_at)"
                          small
                          text
                          color="green darken-1"
                          @click="showTrackingProgress(sib)"
                          class="action-btn-group__btn"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small left>mdi-truck-delivery</v-icon>
                          Track
                        </v-btn>
                        <v-btn
                          v-else
                          small
                          text
                          color="teal darken-1"
                          @click="openShipConfirm(sib)"
                          class="action-btn-group__btn"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small left>mdi-truck-fast</v-icon>
                          Ship
                        </v-btn>
                      </template>
                      <span>{{ isOrderBooked(sib) || sib.order_status === 11 ? 'Track Progress' : 'Ship Order' }}</span>
                    </v-tooltip>

                    <v-tooltip v-if="!isSeller" bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          small
                          text
                          :color="sib.order_status === 0 ? 'primary' : 'grey darken-1'"
                          @click="navigateToOrder(sib.id)"
                          class="action-btn-group__btn"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>{{ sib.order_status === 0 ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ sib.order_status === 0 ? 'Edit Order' : 'View Order (read-only)' }}</span>
                    </v-tooltip>

                    <v-menu bottom left>
                      <template #activator="{ on, attrs }">
                        <v-btn small text color="grey darken-2" class="action-btn-group__btn" v-bind="attrs" v-on="on" title="More actions">
                          <v-icon small>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item :to="`/orders/${encryptIt(sib.id)}/items`">
                          <v-list-item-icon class="mr-2"><v-icon x-small color="info">mdi-format-list-bulleted</v-icon></v-list-item-icon>
                          <v-list-item-content><v-list-item-title class="text-caption">Order Items</v-list-item-title></v-list-item-content>
                        </v-list-item>
                        <v-list-item
                          v-if="sib.order_status !== 11 && !(sib.order_status === 7 && sib.cancelled_at)"
                          @click="generateInvoice(sib.id)"
                        >
                          <v-list-item-icon class="mr-2"><v-icon x-small color="blue">mdi-download</v-icon></v-list-item-icon>
                          <v-list-item-content><v-list-item-title class="text-caption">Download Invoice</v-list-item-title></v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="sib.label_pdf_url && sib.order_status < 6" @click="printLabel(sib.id)">
                          <v-list-item-icon class="mr-2"><v-icon x-small color="teal">mdi-printer</v-icon></v-list-item-icon>
                          <v-list-item-content><v-list-item-title class="text-caption">Print Label</v-list-item-title></v-list-item-content>
                        </v-list-item>

                        <v-divider v-if="!isSeller"></v-divider>
                        <v-list-item v-if="!isSeller" @click="openStatusUpdate(sib)">
                          <v-list-item-icon class="mr-2"><v-icon x-small color="orange">mdi-update</v-icon></v-list-item-icon>
                          <v-list-item-content><v-list-item-title class="text-caption">Update Status</v-list-item-title></v-list-item-content>
                        </v-list-item>
                        <v-list-item
                          v-if="sib.delivery && ['booked', 'manifested', 'confirmed'].includes((sib.delivery.status || '').toLowerCase())"
                          @click="openCancelShipmentConfirm(sib)"
                        >
                          <v-list-item-icon class="mr-2"><v-icon x-small color="red">mdi-cancel</v-icon></v-list-item-icon>
                          <v-list-item-content><v-list-item-title class="text-caption red--text">Cancel Shipment</v-list-item-title></v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="isSeller && sib.order_status < 3 && sib.order_status !== 11" @click="openRejectDialog(sib)">
                          <v-list-item-icon class="mr-2"><v-icon x-small color="red">mdi-close-circle</v-icon></v-list-item-icon>
                          <v-list-item-content><v-list-item-title class="text-caption red--text">Reject Order</v-list-item-title></v-list-item-content>
                        </v-list-item>

                        <v-divider v-if="!isSeller"></v-divider>
                        <v-list-item v-if="!isSeller" @click="showDeleteConfirmation(sib)" class="error--text">
                          <v-list-item-icon class="mr-2"><v-icon x-small color="red">mdi-delete</v-icon></v-list-item-icon>
                          <v-list-item-content><v-list-item-title class="text-caption">Delete Order</v-list-item-title></v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>

                  <v-btn
                    icon
                    small
                    :title="isSellerItemsCollapsed(sib.id) ? 'Show items' : 'Hide items'"
                    @click="toggleSellerItems(sib.id)"
                  >
                    <v-icon>{{ isSellerItemsCollapsed(sib.id) ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                  </v-btn>
                </div>
              </div>

              <div
                v-if="sib.delivery && sib.delivery.awb_number"
                class="text-caption grey--text px-1 pb-1 d-flex align-center flex-wrap"
              >
                <v-icon x-small class="mr-1">mdi-barcode</v-icon>
                <span class="font-weight-medium mr-1">AWB:</span>
                <span style="word-break: break-all">{{ sib.delivery.awb_number }}</span>
                <span v-if="sib.delivery.courier_name" class="ml-2">({{ sib.delivery.courier_name }})</span>
              </div>

              <div
                v-if="isSellerItemsCollapsed(sib.id)"
                class="text-caption grey--text px-1 pb-1"
                style="cursor:pointer"
                @click="toggleSellerItems(sib.id)"
              >
                {{ sib.order_items ? sib.order_items.length : 0 }} item{{ sib.order_items && sib.order_items.length === 1 ? '' : 's' }} &middot; click to view
              </div>

              <v-simple-table v-else dense class="items-mini-table">
                <template #default>
                  <thead>
                    <tr>
                      <th class="text-caption">Product</th>
                      <th class="text-caption text-center">Qty</th>
                      <th class="text-caption text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="oi in sib.order_items" :key="oi.id">
                      <td class="text-caption">{{ getItemName(oi) }}</td>
                      <td class="text-caption text-center">{{ oi.quantity }}</td>
                      <td class="text-caption text-right">₹{{ parseFloat(oi.final_amount || 0).toFixed(2) }}</td>
                    </tr>
                    <tr v-if="!sib.order_items || sib.order_items.length === 0">
                      <td colspan="3" class="text-caption grey--text text-center">No items</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this order?
              </h3>

              <div
                class="order-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3" color="primary">
                    <v-icon color="white" size="24">mdi-receipt</v-icon>
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{ orderToDelete ? orderToDelete.order_number : "" }}
                    </div>
                    <div class="text-caption grey--text">
                      ID: {{ orderToDelete ? orderToDelete.id : "" }}
                    </div>
                    <div class="text-caption grey--text">
                      Amount: ${{
                        orderToDelete
                          ? parseFloat(orderToDelete.final_amount || 0).toFixed(2)
                          : ""
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will move the order to
                the trash.
              </p>
              <p class="text-body-2 grey--text">
                You can restore it later from the deleted orders section.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="cancelDelete"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="confirmDelete"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Order
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Seller Date Filter Dialog -->
      <v-dialog v-model="showSellerDateFilterDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline orange white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-store-outline</v-icon>
            Export Seller Orders
          </v-card-title>
          <v-card-text class="pt-4">
            <div class="text-center mb-4">
              <v-icon size="64" color="orange" class="mb-3">mdi-microsoft-excel</v-icon>
              <h3 class="mb-3">Select Filters for Seller Order Export</h3>
              <p class="text-body-2 grey--text mb-2">
                Choose date range and seller to filter orders for the export.
              </p>
              <p class="text-caption orange--text">
                <v-icon small color="orange" class="mr-1">mdi-information</v-icon>
                Both dates are required for filtering, or leave both empty for all orders.
              </p>
            </div>
            
            <!-- Seller Selection (admin only) -->
            <v-row v-if="!isSeller" class="mb-3">
              <v-col cols="12">
                <v-autocomplete
                  v-model="sellerDateFilter.sellerId"
                  :items="orderSellers"
                  item-text="name"
                  item-value="id"
                  label="Select Seller (Optional)"
                  prepend-icon="mdi-store-outline"
                  outlined
                  dense
                  clearable
                  :loading="isLoadingSellers"
                >
                  <template #item="{ item }">
                    <div class="d-flex align-center w-100">
                      <v-avatar size="24" class="mr-2" color="orange">
                        <span class="white--text text-caption">
                          {{ item.name.charAt(0).toUpperCase() }}
                        </span>
                      </v-avatar>
                      <div class="flex-grow-1">
                        <div class="text-body-2">{{ item.name }}</div>
                        <div class="text-caption grey--text">{{ item.orderCount }} orders</div>
                      </div>
                    </div>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <!-- Date Range Selection -->
            <v-row>
              <v-col cols="6">
                <v-menu
                  v-model="sellerFromMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="sellerDateFilter.fromDate"
                      label="From Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      clearable
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="sellerDateFilter.fromDate"
                    :max="sellerDateFilter.toDate || undefined"
                    no-title
                    scrollable
                    @input="sellerFromMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu
                  v-model="sellerToMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="sellerDateFilter.toDate"
                      label="To Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      clearable
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="sellerDateFilter.toDate"
                    :min="sellerDateFilter.fromDate || undefined"
                    no-title
                    scrollable
                    @input="sellerToMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <div v-if="getSellerExportSummary()" class="mt-3 pa-3 blue-grey lighten-5 rounded">
              <v-icon small class="mr-2">mdi-information</v-icon>
              <span class="text-body-2">{{ getSellerExportSummary() }}</span>
            </div>
          </v-card-text>
          
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="cancelSellerDateFilter">
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn 
              color="orange" 
              @click="exportSellerOrdersWithDates"
              :loading="isSellerExporting"
              :disabled="!isValidSellerDateRange()"
            >
              <v-icon left>mdi-download</v-icon>
              Export Excel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Doctor Payout Export Dialog -->
      <v-dialog v-model="showDoctorPayoutDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline teal white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-doctor</v-icon>
            Export Doctor Payout
          </v-card-title>
          <v-card-text class="pt-4">
            <div class="text-center mb-4">
              <v-icon size="64" color="teal" class="mb-3">mdi-microsoft-excel</v-icon>
              <h3 class="mb-3">Select Filters for Doctor Payout Export</h3>
              <p class="text-body-2 grey--text mb-2">
                Choose date range and doctor to filter orders for export.
              </p>
              <p class="text-caption teal--text">
                <v-icon small color="teal" class="mr-1">mdi-information</v-icon>
                Both dates are required for filtering, or leave both empty for all records.
              </p>
            </div>

            <!-- Doctor Selection -->
            <v-row class="mb-3">
              <v-col cols="12">
                <v-autocomplete
                  v-model="doctorPayoutFilter.doctorId"
                  :items="payoutDoctors"
                  item-text="name"
                  item-value="id"
                  label="Select Doctor (Optional)"
                  prepend-icon="mdi-doctor"
                  outlined
                  dense
                  clearable
                  :loading="isLoadingDoctors"
                >
                  <template #item="{ item }">
                    <div class="d-flex align-center w-100">
                      <v-avatar size="24" class="mr-2" color="teal">
                        <span class="white--text text-caption">
                          {{ item.name.charAt(0).toUpperCase() }}
                        </span>
                      </v-avatar>
                      <div class="flex-grow-1">
                        <div class="text-body-2">{{ item.name }}</div>
                        <div class="text-caption grey--text">{{ item.orderCount }} orders</div>
                      </div>
                    </div>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <!-- Date Range Selection -->
            <v-row>
              <v-col cols="6">
                <v-menu
                  v-model="doctorFromMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="doctorPayoutFilter.fromDate"
                      label="From Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      clearable
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="doctorPayoutFilter.fromDate"
                    :max="doctorPayoutFilter.toDate || undefined"
                    no-title
                    scrollable
                    @input="doctorFromMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu
                  v-model="doctorToMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="doctorPayoutFilter.toDate"
                      label="To Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      clearable
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="doctorPayoutFilter.toDate"
                    :min="doctorPayoutFilter.fromDate || undefined"
                    no-title
                    scrollable
                    @input="doctorToMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <div v-if="getDoctorExportSummary()" class="mt-3 pa-3 blue-grey lighten-5 rounded">
              <v-icon small class="mr-2">mdi-information</v-icon>
              <span class="text-body-2">{{ getDoctorExportSummary() }}</span>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="cancelDoctorPayoutFilter">
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="teal"
              @click="exportDoctorPayoutWithDates"
              :loading="isDoctorExporting"
              :disabled="!isValidDoctorDateRange()"
            >
              <v-icon left>mdi-download</v-icon>
              Export Excel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Track Progress Dialog -->
      <v-dialog v-model="showTrackingDialog" max-width="800" persistent>
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-truck-delivery</v-icon>
            Track Order Progress
            <v-spacer></v-spacer>
            <v-btn icon @click="closeTrackingDialog" color="white">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="px-4 py-4">
            <v-row v-if="selectedOrder">
              <!-- Order Info -->
              <v-col cols="12">
                <v-card outlined>
                  <v-card-subtitle class="pb-2">
                    <div class="d-flex justify-space-between align-center">
                      <span class="text-h6 primary--text">Order #{{ selectedOrder.order_number }}</span>
                      <status-chip :color="getOrderStatusColor(selectedOrder.order_status)" :label="String(getOrderStatusText(selectedOrder.order_status))" />
                    </div>
                    <div class="text-caption grey--text mt-1" v-if="selectedOrder.awb_number || selectedOrder.tracking_number">
                      AWB: {{ selectedOrder.awb_number || selectedOrder.tracking_number }}
                      <span v-if="selectedOrder.courier_name" class="ml-2">({{ selectedOrder.courier_name }})</span>
                    </div>
                  </v-card-subtitle>
                </v-card>
              </v-col>

              <!-- Tabs: Internal vs Nimbus -->
              <v-col cols="12" class="pt-0">
                <v-tabs v-model="trackingTab" color="primary" grow show-arrows>
                  <v-tab>
                    <v-icon left small>mdi-clipboard-text-clock</v-icon>
                    Internal Tracking
                  </v-tab>
                  <v-tab>
                    <v-icon left small>mdi-truck-fast</v-icon>
                    Nimbus Tracking
                  </v-tab>
                </v-tabs>

                <v-tabs-items v-model="trackingTab" class="mt-3">
                  <!-- Internal Tracking Tab -->
                  <v-tab-item :transition="false" :reverse-transition="false">
                    <div class="tracking-container" v-if="trackingHistory.length > 0">
                      <div
                        v-for="(step, index) in getTrackingSteps()"
                        :key="step.step"
                        class="tracking-step"
                        :class="{
                          'completed': step.completed,
                          'current': step.current,
                          'exception': step.isCancelled,
                          'refund': step.isRefund,
                          'rto': step.isRTO
                        }"
                      >
                        <div class="step-indicator">
                          <div
                            class="step-circle"
                            :class="step.isRefund ? 'refund-circle' : getStepCircleClass(step.status, step.current, step.completed)"
                          >
                            <!-- Refund: green check -->
                            <v-icon v-if="step.isRefund" small color="white">mdi-check-circle</v-icon>
                            <!-- Completed: filled check -->
                            <v-icon
                              v-else-if="step.completed && !step.current && !step.isCancelled && !step.isRTO"
                              small color="white"
                            >mdi-check</v-icon>
                            <!-- Current: pulsing dot -->
                            <span v-else-if="step.current" class="current-dot"></span>
                            <!-- Exception -->
                            <v-icon v-else-if="step.isCancelled" small color="white">mdi-close</v-icon>
                            <!-- RTO -->
                            <v-icon v-else-if="step.isRTO" small color="white">mdi-keyboard-return</v-icon>
                            <!-- Pending: step number -->
                            <span v-else class="pending-number">{{ step.step }}</span>
                          </div>
                          <div
                            v-if="index < getTrackingSteps().length - 1"
                            class="step-line"
                            :class="{
                              'completed-line': step.completed && !step.current,
                              'current-line': step.current,
                              'exception-line': step.isCancelled,
                              'rto-line': step.isRTO
                            }"
                          ></div>
                        </div>
                        <div class="step-content">
                          <div class="step-title" :class="{
                            'active-title': step.current,
                            'exception-title': step.isCancelled,
                            'refund-title': step.isRefund,
                            'rto-title': step.isRTO,
                            'pending-title': !step.completed && !step.current && !step.isCancelled && !step.isRefund && !step.isRTO
                          }">
                            {{ step.title }}
                          </div>
                          <div class="step-time" v-if="step.event_time">
                            {{ formatTrackingTime(step.event_time) }}
                          </div>
                          <div class="step-location" v-if="step.location">
                            <v-icon small class="mr-1">mdi-map-marker</v-icon>
                            {{ step.location }}
                          </div>
                          <div class="step-description" v-if="step.note">
                            {{ step.note }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <v-alert v-else-if="!isLoadingTracking" type="info" outlined class="mb-0">
                      No tracking information available for this order.
                    </v-alert>

                    <div v-if="isLoadingTracking" class="text-center py-4">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                      <div class="text-body-2 mt-2">Loading tracking information...</div>
                    </div>
                  </v-tab-item>

                  <!-- Nimbus Tracking Tab -->
                  <v-tab-item :transition="false" :reverse-transition="false">
                    <div v-if="isLoadingTracking" class="text-center py-4">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                      <div class="text-body-2 mt-2">Fetching Nimbus tracking...</div>
                    </div>

                    <template v-else-if="nimbusTracking">
                      <!-- Order Details (top, full width, horizontal grid) -->
                      <v-card outlined class="nimbus-panel mb-3">
                        <div class="nimbus-panel-header indigo darken-2 white--text d-flex align-center">
                          <v-icon small color="white" class="mr-2">mdi-clipboard-text</v-icon>
                          <span>Order Details</span>
                          <v-spacer></v-spacer>
                          <v-chip
                            color="orange darken-1" dark x-small label
                            class="font-weight-bold"
                          >
                            <v-icon left x-small>mdi-truck-fast</v-icon>
                            {{ getNimbusCurrentStageLabel() }}
                          </v-chip>
                        </div>
                        <v-card-text class="pb-3">
                          <v-row dense class="nimbus-detail-grid">
                            <v-col cols="6" sm="4" md="3">
                              <div class="nimbus-detail-label">Order Placed</div>
                              <div class="nimbus-detail-value">{{ formatNimbusEventTime(getNimbusOrderPlacedAt()) || '-' }}</div>
                            </v-col>
                            <v-col cols="6" sm="4" md="3" v-if="nimbusTracking.pickup_date">
                              <div class="nimbus-detail-label">Pickup Date</div>
                              <div class="nimbus-detail-value">{{ formatNimbusEventTime(nimbusTracking.pickup_date) }}</div>
                            </v-col>
                            <v-col cols="6" sm="4" md="3" v-if="nimbusTracking.edd">
                              <div class="nimbus-detail-label">Estimated Delivery</div>
                              <div class="nimbus-detail-value">{{ formatNimbusEventTime(nimbusTracking.edd) }}</div>
                            </v-col>
                            <v-col cols="6" sm="4" md="3" v-if="nimbusTracking.delivered_date">
                              <div class="nimbus-detail-label">Delivered On</div>
                              <div class="nimbus-detail-value">{{ formatNimbusEventTime(nimbusTracking.delivered_date) }}</div>
                            </v-col>
                            <v-col cols="6" sm="4" md="3" v-if="nimbusTracking.courier_name">
                              <div class="nimbus-detail-label">Courier</div>
                              <div class="nimbus-detail-value">{{ nimbusTracking.courier_name }}</div>
                            </v-col>
                            <v-col cols="6" sm="4" md="3" v-if="nimbusTracking.awb_number">
                              <div class="nimbus-detail-label">Tracking ID</div>
                              <div class="nimbus-detail-value font-weight-medium">{{ nimbusTracking.awb_number }}</div>
                            </v-col>
                            <v-col cols="6" sm="4" md="3" v-if="nimbusTracking.order_number">
                              <div class="nimbus-detail-label">Order ID</div>
                              <div class="nimbus-detail-value">{{ nimbusTracking.order_number }}</div>
                            </v-col>
                            <v-col cols="6" sm="4" md="3" v-if="nimbusTracking.payment_type">
                              <div class="nimbus-detail-label">Payment</div>
                              <div class="nimbus-detail-value">
                                <v-chip
                                  x-small label
                                  :color="String(nimbusTracking.payment_type).toLowerCase() === 'prepaid' ? 'green lighten-4' : 'amber lighten-4'"
                                  :class="String(nimbusTracking.payment_type).toLowerCase() === 'prepaid' ? 'green--text text--darken-3' : 'amber--text text--darken-4'"
                                >
                                  {{ String(nimbusTracking.payment_type).toUpperCase() }}
                                </v-chip>
                              </div>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>

                      <!-- Status Delivery (below, full width) -->
                      <v-card outlined class="nimbus-panel">
                        <div class="nimbus-panel-header indigo darken-2 white--text d-flex align-center">
                          <v-icon small color="white" class="mr-2">mdi-truck-delivery</v-icon>
                          <span>Status Delivery</span>
                          <v-spacer></v-spacer>
                          <span class="text-caption font-weight-regular" v-if="nimbusTracking.event_time">
                            <v-icon x-small color="white" class="mr-1">mdi-clock-outline</v-icon>
                            Last update: {{ formatNimbusEventTime(nimbusTracking.event_time) }}
                          </span>
                        </div>
                        <v-card-text>
                          <div class="d-flex align-center mb-4 flex-wrap">
                            <span class="text-h6 font-weight-bold">{{ getNimbusCurrentStageLabel() }}</span>
                            <v-chip
                              v-if="nimbusTracking.status"
                              small label class="ml-3"
                              color="orange lighten-5"
                              text-color="orange darken-3"
                            >
                              {{ String(nimbusTracking.status).toUpperCase() }}
                            </v-chip>
                          </div>

                          <!-- Horizontal 7-step progress -->
                          <div class="nimbus-stepper">
                            <div
                              v-for="(stage, idx) in nimbusStages"
                              :key="stage.key"
                              class="nimbus-step"
                            >
                              <div class="nimbus-step-circle-wrap">
                                <div
                                  v-if="idx > 0"
                                  class="nimbus-step-line"
                                  :class="{ active: idx <= getNimbusCurrentStageIndex() }"
                                ></div>
                                <div
                                  class="nimbus-step-circle"
                                  :class="{
                                    completed: idx < getNimbusCurrentStageIndex(),
                                    current: idx === getNimbusCurrentStageIndex(),
                                  }"
                                >
                                  <v-icon v-if="idx < getNimbusCurrentStageIndex()" small color="white">mdi-check</v-icon>
                                  <v-icon v-else-if="idx === getNimbusCurrentStageIndex()" small color="white">{{ stage.icon }}</v-icon>
                                </div>
                              </div>
                              <div class="nimbus-step-label">{{ stage.label }}</div>
                            </div>
                          </div>

                          <!-- Shipment Progress timeline -->
                          <div class="d-flex align-center mt-6 mb-2">
                            <div class="text-subtitle-1 font-weight-bold">Shipment Progress</div>
                            <v-spacer></v-spacer>
                            <v-chip
                              v-if="Array.isArray(nimbusTracking.history)"
                              small label outlined color="grey darken-1"
                            >
                              {{ nimbusTracking.history.length }} events
                            </v-chip>
                          </div>
                          <v-divider class="mb-3"></v-divider>

                          <v-timeline
                            v-if="Array.isArray(nimbusTracking.history) && nimbusTracking.history.length"
                            dense align-top
                            class="nimbus-timeline"
                          >
                            <v-timeline-item
                              v-for="(event, idx) in nimbusTracking.history"
                              :key="idx"
                              :color="idx === 0 ? nimbusStatusColor(event.status_code) : 'green'"
                              small
                              fill-dot
                            >
                              <div class="nimbus-event-card" :class="{ 'is-latest': idx === 0 }">
                                <div class="d-flex align-center justify-space-between flex-wrap mb-1">
                                  <div class="text-caption grey--text text--darken-1">
                                    <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                                    {{ formatNimbusEventTime(event.event_time) }}
                                  </div>
                                  <v-chip
                                    v-if="event.status_code"
                                    :color="nimbusStatusColor(event.status_code)"
                                    dark x-small label
                                  >
                                    {{ event.status_code }}
                                  </v-chip>
                                </div>
                                <div class="font-weight-bold body-2 mb-1">
                                  {{ nimbusEventTitle(event) }}
                                </div>
                                <div
                                  v-if="event.location"
                                  class="text-caption grey--text text--darken-2 d-flex align-center"
                                >
                                  <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                                  {{ event.location }}
                                </div>
                              </div>
                            </v-timeline-item>
                          </v-timeline>

                          <v-alert v-else type="info" outlined class="mb-0">
                            No status events received from Nimbus yet.
                          </v-alert>
                        </v-card-text>
                      </v-card>
                    </template>

                    <v-alert
                      v-else-if="!selectedOrder.awb_number && !selectedOrder.tracking_number"
                      type="warning" outlined class="mb-0"
                    >
                      This order has no AWB number — Nimbus tracking is unavailable.
                    </v-alert>

                    <v-alert v-else type="info" outlined class="mb-0">
                      Couldn't fetch Nimbus tracking right now. Try Refresh in a moment.
                    </v-alert>
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-btn
              @click="refreshTrackingData"
              color="primary"
              outlined
              :loading="isLoadingTracking"
              :disabled="!selectedOrder"
            >
              <v-icon left>mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn @click="closeTrackingDialog" color="grey" text>
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Update Status Dialog -->
      <v-dialog v-model="showStatusUpdateDialog" max-width="420" persistent>
        <v-card>
          <v-card-title class="text-h6 py-3" :class="statusUpdateForm.status === 11 ? 'error white--text' : 'primary white--text'">
            <v-icon left color="white">{{ statusUpdateForm.status === 11 ? 'mdi-cancel' : 'mdi-update' }}</v-icon>
            {{ statusUpdateForm.status === 11 ? 'Cancel Order' : 'Update Order Status' }}
          </v-card-title>
          <v-card-text class="pt-4">
            <div class="text-caption grey--text mb-3" v-if="statusUpdateOrder">
              Order: <strong>{{ statusUpdateOrder.order_number }}</strong>
            </div>
            <v-autocomplete
              v-model="statusUpdateForm.status"
              :items="(isAdmin || isSuperAdmin)
                ? orderStatusItems
                : (statusUpdateOrder && statusUpdateOrder.order_status < 5
                  ? [{ text: 'Cancelled', value: 11 }]
                  : orderStatusItems)"
              label="Select New Status"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-package-variant"
            ></v-autocomplete>
            <v-alert
              v-if="statusUpdateForm.status === 11 && statusUpdateOrder && statusUpdateOrder.delivery"
              type="warning"
              dense
              outlined
              class="text-caption mt-3"
            >
              This order has a booked shipment. Cancelling the order will also cancel the shipment on Nimbuspost.
            </v-alert>
            <v-alert
              v-else-if="!isAdmin && !isSuperAdmin && statusUpdateOrder && statusUpdateOrder.order_status >= 5"
              type="error"
              dense
              outlined
              class="text-caption mt-3"
            >
              Cannot cancel an order that is already <strong>Out For Delivery</strong> or <strong>Delivered</strong>.
            </v-alert>
            <v-textarea
              v-if="statusUpdateForm.status === 11"
              v-model="statusUpdateForm.cancellation_reason"
              label="Cancellation Reason *"
              outlined
              dense
              rows="3"
              class="mt-3"
              hide-details="auto"
              prepend-inner-icon="mdi-comment-alert-outline"
              placeholder="e.g. Out of Stock, Customer Request, Wrong Address..."
            ></v-textarea>
            <v-textarea
              v-else
              v-model="statusUpdateForm.note"
              label="Note (optional)"
              outlined
              dense
              rows="2"
              class="mt-3"
              hide-details="auto"
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="pb-4 px-4">
            <v-spacer></v-spacer>
            <v-btn text @click="showStatusUpdateDialog = false" :disabled="isStatusUpdating">Close</v-btn>
            <v-btn
              :color="statusUpdateForm.status === 11 ? 'error' : 'primary'"
              :loading="isStatusUpdating"
              :disabled="statusUpdateForm.status === null || (statusUpdateForm.status === 11 && !statusUpdateForm.cancellation_reason)"
              @click="submitStatusUpdate"
            >
              {{ statusUpdateForm.status === 11 ? 'Cancel Order' : 'Update' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Ship Order Confirmation Dialog -->
      <v-dialog v-model="showShipDialog" max-width="420" persistent>
        <v-card>
          <v-card-title class="text-h6 teal darken-1 white--text py-3">
            <v-icon left color="white">mdi-truck-fast</v-icon>
            Ship Order
          </v-card-title>
          <v-card-text class="pt-4">
            <div v-if="shipOrder">
              <p class="mb-2">Ship order <strong>{{ shipOrder.order_number }}</strong>?</p>
              <v-alert
                v-if="shipError"
                type="error"
                dense
                outlined
                class="text-caption mt-2"
              >
                {{ shipError }}
              </v-alert>
            </div>
          </v-card-text>
          <v-card-actions class="pb-4 px-4">
            <v-spacer></v-spacer>
            <v-btn text @click="cancelShipDialog" :disabled="isShipping">Cancel</v-btn>
            <v-btn color="teal darken-1" dark @click="confirmShip" :loading="isShipping" :disabled="isShipping">
              <v-icon left small>mdi-truck-fast</v-icon>
              Ship Now
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Cancel Shipment Confirmation Dialog -->
      <v-dialog v-model="showCancelShipmentDialog" max-width="420" persistent>
        <v-card>
          <v-card-title class="text-h6 error white--text py-3">
            <v-icon left color="white">mdi-cancel</v-icon>
            Cancel Shipment
          </v-card-title>
          <v-card-text class="pt-4">
            <div v-if="cancelShipmentOrder">
              <p class="mb-2">Cancel shipment for order <strong>{{ cancelShipmentOrder.order_number }}</strong>?</p>
              <p class="text-caption grey--text">
                This will cancel the shipment on Nimbuspost and reset the order status to <strong>Pending</strong>.
              </p>
            </div>
          </v-card-text>
          <v-card-actions class="pb-4 px-4">
            <v-spacer></v-spacer>
            <v-btn text @click="cancelCancelShipmentDialog" :disabled="isCancellingShipment">Close</v-btn>
            <v-btn color="error" dark @click="confirmCancelShipment" :loading="isCancellingShipment">
              <v-icon left small>mdi-cancel</v-icon>
              Cancel Shipment
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Reject Order Dialog (Seller) -->
      <v-dialog v-model="showRejectDialog" max-width="460" persistent>
        <v-card>
          <v-card-title class="text-h6 error white--text py-3">
            <v-icon left color="white">mdi-close-circle</v-icon>
            Reject Order
          </v-card-title>
          <v-card-text class="pt-4">
            <div v-if="rejectOrderItem" class="mb-3">
              <v-alert type="warning" outlined dense class="mb-3">
                <strong>{{ rejectOrderItem.order_number }}</strong> will be cancelled.
                <span v-if="rejectOrderItem.payment_type === 2">
                  The customer's payment will be automatically refunded via Razorpay.
                </span>
              </v-alert>
              <v-textarea
                v-model="rejectReason"
                label="Reason for rejection *"
                outlined
                dense
                rows="3"
                hide-details="auto"
                prepend-inner-icon="mdi-comment-alert-outline"
                placeholder="e.g. Out of stock, Cannot fulfil this order..."
                :rules="[v => !!v || 'Reason is required']"
              ></v-textarea>
            </div>
          </v-card-text>
          <v-card-actions class="pb-4 px-4">
            <v-spacer></v-spacer>
            <v-btn text @click="showRejectDialog = false" :disabled="isRejecting">Cancel</v-btn>
            <v-btn
              color="error"
              :loading="isRejecting"
              :disabled="!rejectReason || isRejecting"
              @click="submitReject"
            >
              <v-icon left small>mdi-close-circle</v-icon>
              Reject & Refund
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "OrdersIndex",
  data() {
    return {
      // Table Data
      orders: [],
      expandedGroupRows: [],
      showGroupOrderDialog: false,
      selectedGroupOrder: null,
      collapsedDialogSellers: [],
      count: 0,
      isLoading: false,
      isExcelloading: false,

      // Pagination
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      paginationLength: 0,
      jumpToPage: 1,

      // Search and Filters
      form: {
        search_keyword: "",
        seller_id: null,
        patient_id: null,
        payment_status: null,
        order_status: null,
        order_status_in: null,
        is_active: null,
        customer_city: "",
        date_preset: null,
        start_date: "",
        end_date: "",
        has_group_multi: false,
      },
      searchTimeout: null,

      // Dialog States
      is_deleted_order: false,
      is_restored_order: false,
      showDeleteDialog: false,
      orderToDelete: null,
      isDeleting: false,

      // Seller Date Filter Dialog
      showSellerDateFilterDialog: false,
      isSellerExporting: false,
      isLoadingSellers: false,
      orderSellers: [],
      filterFromMenu: false,
      filterToMenu: false,
      sellerFromMenu: false,
      sellerToMenu: false,
      doctorFromMenu: false,
      doctorToMenu: false,
      sellerDateFilter: {
        sellerId: null,
        fromDate: null,
        toDate: null,
      },

      // Doctor Payout Export
      showDoctorPayoutDialog: false,
      isDoctorExporting: false,
      isLoadingDoctors: false,
      payoutDoctors: [],
      doctorPayoutFilter: {
        doctorId: null,
        fromDate: null,
        toDate: null,
      },

      // Tracking Progress Dialog
      showTrackingDialog: false,
      selectedOrder: null,
      trackingHistory: [],
      isLoadingTracking: false,
      trackingTab: 0,
      nimbusTracking: null,
      nimbusStages: [
        { key: 'booked',     label: 'Booked',                  icon: 'mdi-package-variant-closed', codes: ['BKD', 'ORD', 'shipment_booked', 'booked', 'ordered'] },
        { key: 'pending',    label: 'Pending Pickup',          icon: 'mdi-clock-outline',          codes: ['PP', 'shipment_out_for_pickup', 'pickup_scheduled', 'pending_pickup', 'pending pickup'] },
        { key: 'picked',     label: 'Picked',                  icon: 'mdi-truck',                  codes: ['PD', 'shipment_pickup_complete', 'picked', 'pickup_complete'] },
        { key: 'transit',    label: 'In Transit',              icon: 'mdi-truck-fast',             codes: ['IT', 'shipment_in_transit', 'in_transit', 'in transit'] },
        { key: 'reached',    label: 'Reached at Destination',  icon: 'mdi-map-marker-check',       codes: ['RD', 'shipment_reached_destination', 'reached_destination', 'reached at destination'] },
        { key: 'ofd',        label: 'Out For Delivery',        icon: 'mdi-truck-delivery',         codes: ['OFD', 'shipment_out_for_delivery', 'out_for_delivery', 'out for delivery'] },
        { key: 'delivered',  label: 'Delivered',               icon: 'mdi-check-bold',             codes: ['DL', 'shipment_delivered', 'delivered'] },
      ],

      // Status Update Dialog
      showStatusUpdateDialog: false,
      statusUpdateOrder: null,
      isStatusUpdating: false,
      statusUpdateForm: {
        status: null,
        note: '',
        cancellation_reason: '',
      },

      // Ship Order
      shipError: null,
      showShipDialog: false,
      shipOrder: null,
      isShipping: false,
      shippingOrderId: null,

      // Cancel Shipment
      showCancelShipmentDialog: false,
      cancelShipmentOrder: null,
      isCancellingShipment: false,

      // Reject Order (Seller)
      showRejectDialog: false,
      rejectOrderItem: null,
      rejectReason: '',
      isRejecting: false,

      // Filter Options
      sellers: [],
      patients: [],
      paymentStatusItems: [
        { text: "Cash on Delivery", value: 1 },
        { text: "Prepaid/UPI", value: 2 },
        { text: "Wallet", value: 3 },
      ],
      orderStatusItems: [
        { text: "Pending", value: 0 },
        { text: "Processing", value: 1 },
        { text: "Packed", value: 2 },
        { text: "Out For Pickup", value: 3 },
        { text: "In Transit", value: 4 },
        { text: "Out For Delivery", value: 5 },
        { text: "Delivered", value: 6 },
        { text: "Exception", value: 7 },
        { text: "RTO", value: 8 },
        { text: "RTO In Transit", value: 9 },
        { text: "RTO Delivered", value: 10 },
        { text: "Cancelled", value: 11 },
        { text: "Payment Failed", value: 12 },
      ],
      statusItems: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],

      // Table Headers
      allHeaders: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "50px",
          align: "center",
        },
        { text: "Order", value: "order_info", sortable: false, width: "120px" },
        {
          text: "Customer",
          value: "customer_info",
          sortable: false,
          width: "140px",
          sellerHidden: true,
        },
        {
          text: "Seller",
          value: "seller_info",
          sortable: false,
          width: "140px",
          sellerHidden: true,
        },
        {
          text: "Amount",
          value: "financial_info",
          sortable: false,
          width: "120px",
        },
        {
          text: "Payment Type",
          value: "payment_status",
          sortable: false,
          width: "110px",
          align: "center",
        },
        {
          text: "Order Status",
          value: "order_status",
          sortable: false,
          width: "110px",
          align: "center",
        },
        {
          text: "Delivery",
          value: "delivery_info",
          sortable: false,
          width: "150px",
        },
        {
          text: "Created",
          value: "created_at",
          sortable: false,
          width: "100px",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "120px",
          align: "center",
        },
      ],

      // UI State
      cardHeight: 600,
    };
  },

  computed: {
    isSeller() {
      return this.$store.getters['auth/isSeller'];
    },
    isAdmin() {
      return this.$store.getters['auth/isAdmin'];
    },
    isSuperAdmin() {
      return this.$store.getters['auth/isSuperAdmin'];
    },
    headers() {
      return this.isSeller
        ? this.allHeaders.filter(h => !h.sellerHidden)
        : this.allHeaders;
    },
    groupedOrders() {
      // Tally how many sibling rows actually came back in this response for each
      // order_group — a seller only ever receives their own package from the API
      // (backend scopes by seller_id), so seller_count on the group metadata
      // (the total across ALL sellers in the checkout) can't be used to decide
      // whether to show a "multi-seller" row: it would falsely group a seller's
      // single visible order as if other sellers' packages were shown alongside it.
      const siblingsByGroup = {};
      (this.orders || []).forEach((order) => {
        if (order.order_group) {
          const groupId = order.order_group.id;
          (siblingsByGroup[groupId] = siblingsByGroup[groupId] || []).push(order);
        }
      });

      const emitted = new Set();
      const rows = [];
      (this.orders || []).forEach((order) => {
        const groupId = order.order_group ? order.order_group.id : null;
        if (!groupId) {
          rows.push({ ...order, _isGroup: false, _rowKey: `order-${order.id}`, siblings: [order] });
          return;
        }

        if (emitted.has(groupId)) return;
        emitted.add(groupId);

        const siblings = siblingsByGroup[groupId];
        if (siblings.length > 1) {
          rows.push({
            _isGroup: true,
            _rowKey: `group-${groupId}`,
            id: siblings[0].id,
            order_group: order.order_group,
            order_number: order.order_group.order_number,
            customer: order.customer,
            created_at: order.created_at,
            seller_count: siblings.length,
            siblings,
          });
        } else {
          rows.push({ ...siblings[0], _isGroup: false, _rowKey: `order-${siblings[0].id}`, siblings });
        }
      });
      return rows;
    },
    hasActiveFilters() {
      return (
        this.form.search_keyword ||
        this.form.seller_id ||
        this.form.patient_id ||
        this.form.payment_status ||
        this.form.order_status ||
        this.form.order_status_in ||
        this.form.is_active ||
        this.form.customer_city ||
        this.form.date_preset ||
        this.form.has_group_multi
      );
    },

    visiblePages() {
      const current = this.page;
      const total = this.paginationLength;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];

      for (
        let i = Math.max(2, current - delta);
        i <= Math.min(total - 1, current + delta);
        i++
      ) {
        range.push(i);
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, "...");
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (current + delta < total - 1) {
        rangeWithDots.push("...", total);
      } else if (total > 1) {
        rangeWithDots.push(total);
      }

      return rangeWithDots;
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },

  mounted() {
    this.jumpToPage = this.page;
    this.initStatusFromQuery();
    this.getData();
    this.getSellers();
    this.getPatients();
    this.getUsers();
    this.calculateCardHeight();
    window.addEventListener("resize", this.calculateCardHeight);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.calculateCardHeight);
  },

  methods: {
    initStatusFromQuery() {
      const status = this.$route.query.status;
      if (!status) return;
      const singleMap = {
        pending:   0,
        accepted:  1,
        packed:    2,
        delivered: 6,
        cancelled: 11,
      };
      if (status === 'shipped') {
        this.form.order_status_in = '3,4,5';
        this.form.order_status = null;
      } else if (singleMap[status] !== undefined) {
        this.form.order_status = singleMap[status];
        this.form.order_status_in = null;
      }
    },

    navigateToOrder(orderId) {
      const encryptedId = this.encryptIt(orderId);
      this.$router.push(`/orders/${encryptedId}`);
    },

    navigateToOrderItems(orderId) {
      const encryptedId = this.encryptIt(orderId);
      this.$router.push(`/orders/${encryptedId}/items`);
    },

    async getData() {
      this.isLoading = true;

      try {
        const query = new URLSearchParams({
          page: this.page,
          rowsPerPage: this.itemsPerPage,
          search_keyword: this.form.search_keyword || "",
          seller_id: this.form.seller_id !== null ? this.form.seller_id : "",
          customer_id: this.form.patient_id !== null ? this.form.patient_id : "",
          payment_type:
            this.form.payment_status !== null ? this.form.payment_status : "",
          order_status:
            this.form.order_status_in ? "" : (this.form.order_status !== null ? this.form.order_status : ""),
          order_status_in: this.form.order_status_in || "",
          is_active: this.form.is_active !== null ? this.form.is_active : "",
          customer_city: this.form.customer_city || "",
          start_date: this.form.start_date || "",
          end_date: this.form.end_date || "",
          has_group_multi: this.form.has_group_multi ? 1 : "",
          show_deleted: this.$route.query.open_trash ? 1 : 0,
          context: 'panel',
        });

        const response = await axios.get(`orders?${query}`);
        this.orders = response.data.data;
        this.count = response.data.count;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (error) {
        console.error("Error fetching orders:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      } finally {
        this.isLoading = false;
      }
    },

    async getSellers() {
      // This method is a wrapper - actual logic is in getUsers()
      return;
    },

    async getPatients() {
      // This method is a wrapper - actual logic is in getUsers()
      return;
    },

    async getUsers() {
      try {
        const response = await axios.get("/masters", {
          params: { context: 'orders' }
        });
        const users = response.data.users || [];

        // Reset arrays
        this.sellers = [];
        this.patients = [];

        // Filter users by position
        const sellerUsers = users.filter(
          (user) =>
            user.position &&
            user.position.name &&
            (user.position.name.toLowerCase().includes("seller") ||
              user.position.name.toLowerCase().includes("sales") ||
              user.position.name.toLowerCase().includes("agent"))
        );

        // Populate sellers dropdown
        sellerUsers.forEach((user) => {
          const userName = user.user_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
          this.sellers.push({
            text: userName,
            value: user.id,
          });
        });

        // Populate customers dropdown (all users returned are order customers)
        users.forEach((user) => {
          const userName = user.user_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
          this.patients.push({
            text: userName,
            value: user.id,
          });
        });
      } catch (error) {
        console.error("Error fetching users:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.getData();
      }, 500);
    },

    clearSearch() {
      this.form.search_keyword = "";
      this.page = 1;
      this.getData();
    },

    clearSellerFilter() {
      this.form.seller_id = null;
      this.page = 1;
      this.getData();
    },

    clearPatientFilter() {
      this.form.patient_id = null;
      this.page = 1;
      this.getData();
    },

    clearPaymentStatusFilter() {
      this.form.payment_status = null;
      this.page = 1;
      this.getData();
    },

    clearOrderStatusFilter() {
      this.form.order_status = null;
      this.form.order_status_in = null;
      this.page = 1;
      this.getData();
    },

    clearActiveStatusFilter() {
      this.form.is_active = null;
      this.page = 1;
      this.getData();
    },

    clearAllFilters() {
      this.form.search_keyword = "";
      this.form.seller_id = null;
      this.form.patient_id = null;
      this.form.payment_status = null;
      this.form.order_status = null;
      this.form.order_status_in = null;
      this.form.is_active = null;
      this.form.customer_city = "";
      this.form.date_preset = null;
      this.form.start_date = "";
      this.form.end_date = "";
      this.form.has_group_multi = false;
      this.page = 1;
      this.getData();
    },

    clearCityFilter() {
      this.form.customer_city = "";
      this.page = 1;
      this.getData();
    },

    setDatePreset(preset) {
      this.form.date_preset = preset;
      const today = new Date();
      const fmt = (d) => d.toISOString().split('T')[0];
      if (preset === 'today') {
        this.form.start_date = fmt(today);
        this.form.end_date = fmt(today);
      } else if (preset === 'yesterday') {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        this.form.start_date = fmt(y);
        this.form.end_date = fmt(y);
      } else if (preset === 'this_week') {
        const mon = new Date(today);
        const day = today.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        mon.setDate(today.getDate() + diff);
        this.form.start_date = fmt(mon);
        this.form.end_date = fmt(today);
      } else if (preset === 'this_month') {
        this.form.start_date = fmt(new Date(today.getFullYear(), today.getMonth(), 1));
        this.form.end_date = fmt(today);
      }
      this.page = 1;
      this.getData();
    },

    clearDateFilter() {
      this.form.date_preset = null;
      this.form.start_date = "";
      this.form.end_date = "";
      this.page = 1;
      this.getData();
    },

    changeRowsPerPage() {
      this.page = 1;
      this.getData();
    },

    previousPage() {
      if (this.page > 1) {
        this.page--;
      }
    },

    nextPage() {
      if (this.page < this.paginationLength) {
        this.page++;
      }
    },

    goToPageNumber(pageNumber) {
      if (
        pageNumber !== "..." &&
        pageNumber >= 1 &&
        pageNumber <= this.paginationLength
      ) {
        this.page = pageNumber;
      }
    },

    jumpToPageAction() {
      const pageNum = parseInt(this.jumpToPage);
      if (pageNum && pageNum >= 1 && pageNum <= this.paginationLength) {
        this.page = pageNum;
        this.jumpToPage = null;
      }
    },

    calculateCardHeight() {
      const viewportHeight = window.innerHeight;
      const headerHeight = 64;
      const containerPadding = 48;
      const cardHeaderHeight = 140;
      const paginationHeight = 60;

      this.cardHeight =
        viewportHeight -
        headerHeight -
        containerPadding -
        cardHeaderHeight -
        paginationHeight;

      if (this.cardHeight < 400) {
        this.cardHeight = 400;
      }
    },

    async generateExcel() {
      this.isExcelloading = true;

      try {
        const query = new URLSearchParams({
          search_keyword: this.form.search_keyword || "",
          seller_id: this.form.seller_id !== null ? this.form.seller_id : "",
          customer_id: this.form.patient_id !== null ? this.form.patient_id : "",
          payment_type:
            this.form.payment_status !== null ? this.form.payment_status : "",
          order_status:
            this.form.order_status !== null ? this.form.order_status : "",
          is_active: this.form.is_active !== null ? this.form.is_active : "",
          show_deleted: this.$route.query.open_trash ? 1 : 0,
        });

        const response = await axios.get(`orders/excel?${query}`, {
          responseType: "blob",
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Get filename from response headers or use default
        const contentDisposition = response.headers["content-disposition"];
        let filename = "orders_export.xlsx";
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Excel export failed:", error);
      } finally {
        this.isExcelloading = false;
      }
    },

    showDeleteConfirmation(order) {
      this.orderToDelete = order;
      this.showDeleteDialog = true;
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.orderToDelete = null;
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.orderToDelete) return;

      this.isDeleting = true;

      try {
        await axios.post(`orders/delete/${this.encryptIt(this.orderToDelete.id)}`);
        this.showDeleteDialog = false;
        this.orderToDelete = null;
        this.isDeleting = false;
        this.is_deleted_order = true;
        this.getData();
      } catch (error) {
        console.error("Delete failed:", error);
        this.isDeleting = false;
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },

    async Delete_this(id) {
      try {
        await axios.post(`orders/delete/${this.encryptIt(id)}`);
        this.is_deleted_order = true;
        this.getData();
      } catch (error) {
        console.error("Delete failed:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },

    async Restore_this(id) {
      try {
        await axios.post(`orders/restore/${this.encryptIt(id)}`);
        this.is_restored_order = true;
        this.getData();
      } catch (error) {
        console.error("Restore failed:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },

    isDeletePage() {
      return !!this.$route.query.open_trash;
    },

    getOrderStatusColor(status) {
      if (status === null || status === undefined) status = 0;
      switch (status) {
        case 0: return "warning";     // Pending
        case 1: return "info";        // Processing  
        case 2: return "primary";     // Packed
        case 3: return "purple";      // Out For Pickup
        case 4: return "indigo";      // In Transit
        case 5: return "orange";      // Out For Delivery
        case 6: return "success";     // Delivered
        case 7: return "error";       // Exception
        case 8: return "red darken-2"; // RTO
        case 9: return "red";         // RTO In Transit
        case 10: return "red lighten-1"; // RTO Delivered
        case 11: return "red darken-1";  // Cancelled
        case 12: return "deep-purple";   // Payment Failed
        default: return "grey";
      }
    },

    getOrderStatusIcon(status) {
      if (status === null || status === undefined) status = 0;
      switch (status) {
        case 0: return "mdi-clock";
        case 1: return "mdi-cog";
        case 2: return "mdi-package";
        case 3: return "mdi-truck";
        case 4: return "mdi-transit-connection-variant";
        case 5: return "mdi-truck-delivery";
        case 6: return "mdi-check-circle";
        case 7: return "mdi-alert-circle";
        case 8: return "mdi-keyboard-return";
        case 9: return "mdi-truck-fast";
        case 10: return "mdi-check-circle-outline";
        case 11: return "mdi-cancel";
        case 12: return "mdi-credit-card-off";
        default: return "mdi-help-circle";
      }
    },

    getOrderStatusText(status) {
      const statusMap = {
        0: "Pending",
        1: "Processing",
        2: "Packed",
        3: "Out For Pickup",
        4: "In Transit",
        5: "Out For Delivery",
        6: "Delivered",
        7: "Exception",
        8: "RTO",
        9: "RTO In Transit",
        10: "RTO Delivered",
        11: "Cancelled",
        12: "Payment Failed"
      };
      return statusMap[status ?? 0] ?? "Pending";
    },

    getPaymentTypeColor(type) {
      switch (type) {
        case 1: return "orange";      // COD
        case 2: return "success";     // Prepaid
        case 3: return "purple";      // Wallet
        case 4: return "info";        // Reverse
        default: return "grey";
      }
    },

    getPaymentTypeIcon(type) {
      switch (type) {
        case 1: return "mdi-cash";
        case 2: return "mdi-credit-card";
        case 3: return "mdi-wallet";
        case 4: return "mdi-cash-refund";
        default: return "mdi-help-circle";
      }
    },

    getPaymentTypeText(type) {
      const typeMap = {
        1: "COD",
        2: "Prepaid",
        3: "Wallet", 
        4: "Reverse"
      };
      return typeMap[type] || "Unknown";
    },

    getOrderTotal(order) {
      if (order.order_items && order.order_items.length > 0) {
        const itemsTotal = order.order_items.reduce((sum, item) => {
          return sum + parseFloat(item.final_amount || 0);
        }, 0);
        return itemsTotal + parseFloat(order.delivery_charges || 0);
      }
      return parseFloat(order.final_amount || 0);
    },

    groupTotal(group) {
      return group.siblings.reduce((sum, sib) => sum + this.getOrderTotal(sib), 0);
    },

    groupItemCount(group) {
      return group.siblings.reduce((sum, sib) => sum + (sib.order_items ? sib.order_items.length : 0), 0);
    },

    isGroupStatusMixed(group) {
      const siblings = group.siblings;
      return siblings.some((sib) => sib.order_status !== siblings[0].order_status);
    },

    getGroupStatusList(group) {
      const seen = new Map();
      group.siblings.forEach((sib) => {
        const isCancelled = sib.cancelled_at && sib.order_status === 7;
        const key = isCancelled ? "cancelled" : sib.order_status;
        if (!seen.has(key)) {
          seen.set(key, {
            text: isCancelled ? "Cancelled" : this.getOrderStatusText(sib.order_status),
            color: isCancelled ? "red darken-1" : this.getOrderStatusColor(sib.order_status),
            icon: isCancelled ? "mdi-close-circle" : this.getOrderStatusIcon(sib.order_status),
          });
        }
      });
      return Array.from(seen.values());
    },

    openGroupOrderDialog(item) {
      this.selectedGroupOrder = item;
      this.collapsedDialogSellers = item.siblings.map((sib) => sib.id);
      this.showGroupOrderDialog = true;
    },

    isSellerItemsCollapsed(sibId) {
      return this.collapsedDialogSellers.includes(sibId);
    },

    toggleSellerItems(sibId) {
      const idx = this.collapsedDialogSellers.indexOf(sibId);
      if (idx === -1) {
        this.collapsedDialogSellers.push(sibId);
      } else {
        this.collapsedDialogSellers.splice(idx, 1);
      }
    },

    closeGroupOrderDialog() {
      this.showGroupOrderDialog = false;
      this.selectedGroupOrder = null;
    },

    getItemName(item) {
      return (
        item.seller_product_variant?.product_variant?.name ||
        item.product?.name ||
        "Unknown"
      );
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.form.search_keyword) count++;
      if (this.form.seller_id) count++;
      if (this.form.patient_id) count++;
      if (this.form.payment_status) count++;
      if (this.form.order_status) count++;
      if (this.form.is_active) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        // Reset to current page if invalid input
        this.jumpToPage = this.page;
      }
    },

    async generateInvoice(orderId) {
      try {
        // Show loading state
        this.$toast.info('Generating invoice...');
        
        const response = await axios.get(
          `/orders/${this.encryptIt(orderId)}/invoice`,
          {
            responseType: "blob",
          }
        );

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Get filename from response headers or use default
        const contentDisposition = response.headers["content-disposition"];
        let filename = `order_invoice_${orderId}.pdf`;
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        // Show success message
        this.$toast.success('Invoice downloaded successfully!');
      } catch (error) {
        console.error("Invoice generation failed:", error);
        this.$toast.error('Failed to generate invoice. Please try again.');
      }
    },

    async downloadCustomerInvoice(item) {
      if (!item.order_group || !item.order_group.id) return;

      try {
        this.$toast.info('Generating customer invoice...');

        const response = await axios.get(
          `/order-groups/${this.encryptIt(item.order_group.id)}/invoice`,
          {
            responseType: "blob",
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let filename = `customer_invoice_${item.order_number || item.order_group.id}.pdf`;
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$toast.success('Customer invoice downloaded successfully!');
      } catch (error) {
        console.error("Customer invoice generation failed:", error);
        this.$toast.error('Failed to generate customer invoice. Please try again.');
      }
    },

    async generateSlip(orderId) {
      try {
        // Show loading state
        this.$toast.info('Generating delivery slip...');

        const response = await axios.get(
          `/orders/${this.encryptIt(orderId)}/slip`,
          {
            responseType: "blob",
          }
        );

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Get filename from response headers or use default
        const contentDisposition = response.headers["content-disposition"];
        let filename = `delivery_slip_${orderId}.pdf`;
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // Show success message
        this.$toast.success('Delivery slip downloaded successfully!');
      } catch (error) {
        console.error("Delivery slip generation failed:", error);
        this.$toast.error('Failed to generate delivery slip. Please try again.');
      }
    },
    async printLabel(orderId) {
      try {
        this.$toast.info('Fetching shipping label...');

        const response = await axios.get(
          `/orders/${this.encryptIt(orderId)}/label`,
          { responseType: "blob" }
        );

        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let filename = `label_${orderId}.pdf`;
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$toast.success('Shipping label downloaded successfully!');
      } catch (error) {
        console.error("Print label failed:", error);
        const status = error?.response?.status;
        if (status === 404) {
          this.$toast.error('No shipping label is available yet. Ship the order first.');
        } else {
          this.$toast.error('Failed to download shipping label. Please try again.');
        }
      }
    },

    // Downloads every sibling seller's invoice for a multi-seller checkout —
    // each package keeps its own GST invoice, this just fetches them all in one go.
    async downloadAllGroupDocuments(item) {
      if (this.isSeller || !item.order_group || item.order_group.seller_count <= 1) return;

      try {
        this.$toast.info('Fetching checkout packages...');
        const response = await axios.get(`/order-groups/${this.encryptIt(item.order_group.id)}`);
        const orders = response.data?.data?.orders || [];

        if (!orders.length) {
          this.$toast.error('No packages found for this checkout.');
          return;
        }

        for (const order of orders) {
          await this.generateInvoice(order.id);
        }
      } catch (error) {
        console.error("Fetching checkout packages failed:", error);
        this.$toast.error('Failed to fetch this checkout\'s packages. Please try again.');
      }
    },

    async openSellerDateFilterDialog() {
      this.showSellerDateFilterDialog = true;
      if (!this.isSeller) {
        await this.loadOrderSellers();
      }
    },

    async loadOrderSellers() {
      this.isLoadingSellers = true;
      try {
        const response = await axios.get('orders/sellers_with_orders');
        if (response.data.success) {
          this.orderSellers = [
            { id: null, name: 'All Sellers', orderCount: 'All' },
            ...response.data.data.map(seller => ({
              id: seller.id,
              name: seller.first_name && seller.last_name 
                ? `${seller.first_name} ${seller.last_name}` 
                : seller.user_name || seller.email,
              orderCount: seller.orders_count
            }))
          ];
        }
      } catch (error) {
        console.error('Error fetching order sellers:', error);
        this.$toast.error('Failed to load sellers');
      } finally {
        this.isLoadingSellers = false;
      }
    },

    cancelSellerDateFilter() {
      this.showSellerDateFilterDialog = false;
      this.sellerDateFilter.fromDate = null;
      this.sellerDateFilter.toDate = null;
      this.sellerDateFilter.sellerId = null;
    },

    isValidSellerDateRange() {
      // Both dates required together or both empty
      const hasFromDate = !!this.sellerDateFilter.fromDate;
      const hasToDate = !!this.sellerDateFilter.toDate;
      
      // Valid if both empty (no date filter) or both filled
      return (!hasFromDate && !hasToDate) || (hasFromDate && hasToDate);
    },

    getSellerExportSummary() {
      let summary = '';
      
      // Seller filter summary
      const selectedSeller = this.orderSellers.find(s => s.id === this.sellerDateFilter.sellerId);
      if (selectedSeller && selectedSeller.id !== null) {
        summary += `Seller: ${selectedSeller.name}. `;
      } else {
        summary += 'All sellers. ';
      }
      
      // Date filter summary
      if (this.sellerDateFilter.fromDate && this.sellerDateFilter.toDate) {
        summary += `Date range: ${this.sellerDateFilter.fromDate} to ${this.sellerDateFilter.toDate}.`;
      } else {
        summary += 'All dates.';
      }
      
      return summary;
    },

    async exportSellerOrdersWithDates() {
      this.isSellerExporting = true;
      
      try {
        let query = `show_deleted=${this.$route.query.open_trash ? 1 : 0}`;
        
        // Add date filters if both dates are provided
        if (this.sellerDateFilter.fromDate && this.sellerDateFilter.toDate) {
          query += `&start_date=${this.sellerDateFilter.fromDate}`;
          query += `&end_date=${this.sellerDateFilter.toDate}`;
        }
        
        // Add seller filter — auto-use logged-in user ID for sellers
        if (this.isSeller) {
          query += `&seller_id=${this.$store.state.auth.user?.id}`;
        } else if (this.sellerDateFilter.sellerId) {
          query += `&seller_id=${this.sellerDateFilter.sellerId}`;
        }

        const response = await axios.get(`orders/seller_excel?${query}`, {
          responseType: "blob",
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        
        // Generate filename with filters
        let filename = "seller_orders_";
        
        // Add seller to filename if selected
        if (this.sellerDateFilter.sellerId) {
          const selectedSeller = this.orderSellers.find(s => s.id === this.sellerDateFilter.sellerId);
          if (selectedSeller) {
            const sellerName = selectedSeller.name.replace(/[^a-zA-Z0-9]/g, '_');
            filename += `${sellerName}_`;
          }
        }
        
        // Add date range to filename if provided
        if (this.sellerDateFilter.fromDate && this.sellerDateFilter.toDate) {
          filename += `${this.sellerDateFilter.fromDate}_to_${this.sellerDateFilter.toDate}_`;
        }
        
        filename += `${new Date().toISOString().slice(0, 10)}.xlsx`;
        
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        // Close dialog and reset filters
        this.showSellerDateFilterDialog = false;
        this.sellerDateFilter.fromDate = null;
        this.sellerDateFilter.toDate = null;
        this.sellerDateFilter.sellerId = null;
        
        // Show success message
        this.$toast.success('Seller order report downloaded successfully!');
      } catch (error) {
        console.error("Seller export failed:", error);
        this.$toast.error('Failed to export seller orders');
      } finally {
        this.isSellerExporting = false;
      }
    },

    // Doctor Payout Export Methods
    async openDoctorPayoutDialog() {
      this.showDoctorPayoutDialog = true;
      await this.loadPayoutDoctors();
    },

    async loadPayoutDoctors() {
      this.isLoadingDoctors = true;
      try {
        const response = await axios.get('orders/doctors_with_orders');
        if (response.data.success) {
          this.payoutDoctors = [
            { id: null, name: 'All Doctors', orderCount: 'All' },
            ...response.data.data.map(doctor => ({
              id: doctor.id,
              name: doctor.first_name && doctor.last_name
                ? `${doctor.first_name} ${doctor.last_name}`
                : doctor.user_name || doctor.email,
              orderCount: doctor.orders_count
            }))
          ];
        }
      } catch (error) {
        console.error('Error fetching payout doctors:', error);
        this.$toast.error('Failed to load doctors');
      } finally {
        this.isLoadingDoctors = false;
      }
    },

    cancelDoctorPayoutFilter() {
      this.showDoctorPayoutDialog = false;
      this.doctorPayoutFilter.fromDate = null;
      this.doctorPayoutFilter.toDate = null;
      this.doctorPayoutFilter.doctorId = null;
    },

    isValidDoctorDateRange() {
      const hasFromDate = !!this.doctorPayoutFilter.fromDate;
      const hasToDate = !!this.doctorPayoutFilter.toDate;
      return (!hasFromDate && !hasToDate) || (hasFromDate && hasToDate);
    },

    getDoctorExportSummary() {
      let summary = '';

      const selectedDoctor = this.payoutDoctors.find(d => d.id === this.doctorPayoutFilter.doctorId);
      if (selectedDoctor && selectedDoctor.id !== null) {
        summary += `Doctor: ${selectedDoctor.name}. `;
      } else {
        summary += 'All doctors. ';
      }

      if (this.doctorPayoutFilter.fromDate && this.doctorPayoutFilter.toDate) {
        summary += `Date range: ${this.doctorPayoutFilter.fromDate} to ${this.doctorPayoutFilter.toDate}.`;
      } else {
        summary += 'All dates.';
      }

      return summary;
    },

    async exportDoctorPayoutWithDates() {
      this.isDoctorExporting = true;

      try {
        let query = '';

        if (this.doctorPayoutFilter.fromDate && this.doctorPayoutFilter.toDate) {
          query += `start_date=${this.doctorPayoutFilter.fromDate}`;
          query += `&end_date=${this.doctorPayoutFilter.toDate}`;
        }

        if (this.doctorPayoutFilter.doctorId) {
          query += query ? '&' : '';
          query += `doctor_id=${this.doctorPayoutFilter.doctorId}`;
        }

        const response = await axios.get(`orders/doctor_payout_excel${query ? '?' + query : ''}`, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        let filename = "doctor_payout_";

        if (this.doctorPayoutFilter.doctorId) {
          const selectedDoctor = this.payoutDoctors.find(d => d.id === this.doctorPayoutFilter.doctorId);
          if (selectedDoctor) {
            const doctorName = selectedDoctor.name.replace(/[^a-zA-Z0-9]/g, '_');
            filename += `${doctorName}_`;
          }
        }

        if (this.doctorPayoutFilter.fromDate && this.doctorPayoutFilter.toDate) {
          filename += `${this.doctorPayoutFilter.fromDate}_to_${this.doctorPayoutFilter.toDate}_`;
        }

        filename += `${new Date().toISOString().slice(0, 10)}.xlsx`;

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.showDoctorPayoutDialog = false;
        this.doctorPayoutFilter.fromDate = null;
        this.doctorPayoutFilter.toDate = null;
        this.doctorPayoutFilter.doctorId = null;

        this.$toast.success('Doctor payout report downloaded successfully!');
      } catch (error) {
        console.error("Doctor payout export failed:", error);
        this.$toast.error('Failed to export doctor payout');
      } finally {
        this.isDoctorExporting = false;
      }
    },

    // Tracking Progress Methods
    async showTrackingProgress(order) {
      this.selectedOrder = order;
      this.showTrackingDialog = true;
      await this.loadTrackingHistory(order);
    },

    async loadTrackingHistory(order) {
      this.isLoadingTracking = true;
      this.trackingHistory = [];
      this.nimbusTracking = null;
      try {
        const response = await axios.get(`order_trackings?order_id=${order.id}`);
        if (response.data.success) {
          this.trackingHistory = (response.data.data || []).slice().sort((a, b) => a.step - b.step);
          this.nimbusTracking = response.data.nimbus_tracking || null;
          // Update selectedOrder and the matching row in the orders table with the live status
          const liveOrder = response.data.order;
          const liveStatus = response.data.order_status;
          if (liveStatus !== null && liveStatus !== undefined) {
            this.selectedOrder = {
              ...this.selectedOrder,
              order_status: liveStatus,
              awb_number: liveOrder?.awb_number || this.selectedOrder.awb_number,
              courier_name: liveOrder?.courier_name || this.selectedOrder.courier_name,
            };
            const idx = this.orders.findIndex(o => o.id === this.selectedOrder.id);
            if (idx !== -1) {
              this.$set(this.orders, idx, { ...this.orders[idx], order_status: liveStatus });
            }
          }
        }
      } catch (error) {
        console.error("Error loading tracking history:", error);
      } finally {
        this.isLoadingTracking = false;
      }
    },

    async refreshTrackingData() {
      if (!this.selectedOrder) return;
      await this.loadTrackingHistory(this.selectedOrder);
    },

    closeTrackingDialog() {
      this.showTrackingDialog = false;
      this.selectedOrder = null;
      this.trackingHistory = [];
      this.nimbusTracking = null;
      this.trackingTab = 0;
    },

    formatNimbusEventTime(value) {
      if (!value) return '';
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    getNimbusCurrentStageIndex() {
      if (!this.nimbusTracking) return -1;
      const history = Array.isArray(this.nimbusTracking.history) ? this.nimbusTracking.history : [];
      // Nimbus returns history newest-first; index 0 is the latest event
      const probeCodes = [];
      if (history.length) {
        probeCodes.push(history[0].status_code, history[0].status);
      }
      probeCodes.push(this.nimbusTracking.status_code, this.nimbusTracking.status);

      for (const raw of probeCodes) {
        if (!raw) continue;
        const code = String(raw).toLowerCase();
        const idx = this.nimbusStages.findIndex(stage =>
          stage.codes.some(c => c.toLowerCase() === code)
        );
        if (idx !== -1) return idx;
      }

      // Fallback: highest stage seen anywhere in history
      let highest = -1;
      for (const event of history) {
        const code = String(event.status_code || event.status || '').toLowerCase();
        const idx = this.nimbusStages.findIndex(stage =>
          stage.codes.some(c => c.toLowerCase() === code)
        );
        if (idx > highest) highest = idx;
      }
      return highest;
    },

    getNimbusCurrentStageLabel() {
      const idx = this.getNimbusCurrentStageIndex();
      if (idx >= 0) return this.nimbusStages[idx].label;
      return this.nimbusTracking?.status || 'Tracking';
    },

    getNimbusOrderPlacedAt() {
      const history = this.nimbusTracking?.history;
      if (Array.isArray(history) && history.length) {
        // History is newest-first; the oldest event is the booking
        return history[history.length - 1]?.event_time || null;
      }
      return this.nimbusTracking?.created || this.nimbusTracking?.created_at || null;
    },

    nimbusCodeLabel(code) {
      const map = {
        PP: 'Pending Pickup',
        PD: 'Picked',
        IT: 'In Transit',
        OFD: 'Out For Delivery',
        DL: 'Delivered',
        EX: 'Exception',
        CAN: 'Cancelled',
        RT: 'RTO',
        'RT-IT': 'RTO In Transit',
        'RT-DL': 'RTO Delivered',
        PKD: 'Packed',
        PRC: 'Processing',
        ORD: 'Ordered',
        BKD: 'Booked',
        RD: 'Reached at Destination',
      };
      return map[code] || (code ? String(code).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '');
    },

    nimbusEventTitle(event) {
      const msg = (event?.message || '').trim();
      // Skip placeholder messages from Nimbus
      const isPlaceholder = !msg || /^expected at\s*null$/i.test(msg) || /^expected at\s*$/i.test(msg);
      if (isPlaceholder) {
        return this.nimbusCodeLabel(event?.status_code) || 'Status update';
      }
      // Format snake_case payloads like "shipment_pickup_complete" → "Shipment Pickup Complete"
      if (/^[a-z0-9_]+$/.test(msg)) {
        return msg.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      }
      return msg;
    },

    nimbusStatusColor(code) {
      const map = {
        DL: 'success',
        OFD: 'orange',
        IT: 'blue',
        PD: 'blue',
        PP: 'amber darken-2',
        PKD: 'cyan darken-1',
        PRC: 'indigo',
        ORD: 'grey darken-1',
        EX: 'error',
        CAN: 'error',
        RT: 'red darken-2',
        'RT-IT': 'red darken-2',
        'RT-DL': 'red darken-2',
      };
      return map[code] || 'grey';
    },

    openStatusUpdate(order) {
      this.statusUpdateOrder = order;
      this.statusUpdateForm.status = order.order_status ?? null;
      this.statusUpdateForm.note = '';
      this.statusUpdateForm.cancellation_reason = '';
      this.showStatusUpdateDialog = true;
    },

    async submitStatusUpdate() {
      if (this.statusUpdateForm.status === null) return;
      if (this.statusUpdateForm.status === 11 && !this.statusUpdateForm.cancellation_reason) return;
      this.isStatusUpdating = true;
      try {
        const payload = { status: this.statusUpdateForm.status };
        if (this.statusUpdateForm.status === 11) {
          payload.cancellation_reason = this.statusUpdateForm.cancellation_reason;
        } else {
          payload.note = this.statusUpdateForm.note || undefined;
        }
        await axios.patch(`orders/${this.encryptIt(this.statusUpdateOrder.id)}/status`, payload);
        this.showStatusUpdateDialog = false;
        this.statusUpdateOrder = null;
        this.getData();
      } catch (e) {
        // error handled by axios interceptor
      } finally {
        this.isStatusUpdating = false;
      }
    },

    openShipConfirm(order) {
      this.shipOrder = order;
      this.shipError = null;
      this.showShipDialog = true;
    },

    cancelShipDialog() {
      this.showShipDialog = false;
      this.shipOrder = null;
      this.shipError = null;
    },

    async confirmShip() {
      if (!this.shipOrder) return;
      this.isShipping = true;
      this.shipError = null;
      this.shippingOrderId = this.shipOrder.id;
      try {
        await axios.post(`orders/${this.encryptIt(this.shipOrder.id)}/ship`);
        this.$toast.success('Shipment created and manifested successfully');
        this.showShipDialog = false;
        this.shipOrder = null;
        this.shipError = null;
        this.getData();
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to ship order';
        this.shipError = msg;
      } finally {
        this.isShipping = false;
        this.shippingOrderId = null;
      }
    },

    openCancelShipmentConfirm(order) {
      this.cancelShipmentOrder = order;
      this.showCancelShipmentDialog = true;
    },

    cancelCancelShipmentDialog() {
      this.showCancelShipmentDialog = false;
      this.cancelShipmentOrder = null;
    },

    async confirmCancelShipment() {
      if (!this.cancelShipmentOrder) return;
      this.isCancellingShipment = true;
      try {
        await axios.post(`orders/${this.encryptIt(this.cancelShipmentOrder.id)}/cancel-shipment`);
        this.$toast.success('Shipment cancelled successfully');
        this.showCancelShipmentDialog = false;
        this.cancelShipmentOrder = null;
        this.getData();
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to cancel shipment';
        this.$toast.error(msg);
      } finally {
        this.isCancellingShipment = false;
      }
    },

    openRejectDialog(item) {
      this.rejectOrderItem = item;
      this.rejectReason = '';
      this.showRejectDialog = true;
    },

    async submitReject() {
      if (!this.rejectOrderItem || !this.rejectReason) return;
      this.isRejecting = true;
      try {
        const response = await axios.patch(`orders/${this.encryptIt(this.rejectOrderItem.id)}/status`, {
          status: 11,
          cancellation_reason: this.rejectReason,
        });
        const refund = response.data?.data?.refund;
        const msg = refund?.attempted
          ? `Order rejected. ${refund.message}`
          : 'Order rejected successfully.';
        this.$toast.success(msg);
        this.showRejectDialog = false;
        this.rejectOrderItem = null;
        this.rejectReason = '';
        this.getData();
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to reject order';
        this.$toast.error(msg);
      } finally {
        this.isRejecting = false;
      }
    },

    isOrderBooked(item) {
      return !!(item.delivery && item.delivery.awb_number);
    },

    getDeliveryStatusColor(status) {
      const map = {
        confirmed: 'blue',
        booked: 'teal darken-1',
        manifested: 'teal',
        pending: 'orange',
        cancelled: 'error',
        delivered: 'success',
        failed: 'red',
      };
      return map[(status || '').toLowerCase()] || 'grey';
    },

    getTrackingStatusColor(step) {
      const colorMap = {
        1: 'orange',     // Order Placed
        2: 'blue',       // Processing
        3: 'purple',     // Packed
        4: 'amber',      // Out for Delivery
        5: 'green',      // Delivered
        6: 'deep-orange', // RTO
        7: 'red'         // Cancelled
      };
      return colorMap[step] || 'grey';
    },

    getTrackingStatusText(status) {
      const statusMap = {
        0: 'Order Placed',
        1: 'Processing',
        2: 'Packed',
        3: 'Out for Pickup',
        4: 'In Transit',
        5: 'Out for Delivery',
        6: 'Delivered',
        7: 'Exception',
        8: 'RTO',
        9: 'RTO In Transit',
        10: 'RTO Delivered',
        11: 'Order Cancelled',
      };
      return statusMap[status] ?? 'Status Update';
    },

    getTrackingSteps() {
      if (!this.trackingHistory || this.trackingHistory.length === 0) return [];

      const sortedHistory = [...this.trackingHistory].sort((a, b) => a.step - b.step);
      const currentOrderStatus = this.selectedOrder?.order_status ?? 0;

      // Cancelled = explicit status 11, or status 7 when order has cancelled_at
      const isCancelledOrder = currentOrderStatus === 11 ||
        (currentOrderStatus === 7 && !!(this.selectedOrder?.cancelled_at));
      // For cancelled orders only show Order Placed + Cancelled/Exception + refund steps
      const filteredHistory = isCancelledOrder
        ? sortedHistory.filter(r => r.status === 0 || r.status >= 7)
        : sortedHistory;

      return filteredHistory.map((record) => {
        const msg = (record.message || '').toLowerCase();
        const isRefund   = record.status === 7 && msg.includes('refund');
        const isCancelled = record.status === 11 || (record.status === 7 && !isRefund);
        const isRTO = [8, 9, 10].includes(record.status);
        const isCompleted = isCancelled || isRTO || isRefund || currentOrderStatus >= record.status;
        const isCurrent   = !isCancelled && !isRTO && !isRefund && currentOrderStatus === record.status;

        return {
          status: record.status,
          title: (record.message && record.message.toLowerCase() !== 'status updated')
            ? record.message
            : this.getTrackingStatusText(record.status),
          completed: isCompleted,
          current: isCurrent,
          isException: isCancelled,
          isRTO: isRTO,
          isCancelled: isCancelled,
          isRefund: isRefund,
          step: record.step,
          event_time: isCompleted ? record.event_time : null,
          formatted_event_time: record.formatted_event_time,
          location: isCompleted ? record.location : null,
          message: record.message,
          note: record.note,
          trackingRecord: record
        };
      });
    },

    isTrackingStepCompleted(stepStatus) {
      const steps = this.getTrackingSteps();
      const step = steps.find(s => s.status === stepStatus);
      return step ? step.completed : false;
    },

    isTrackingStepCurrent(stepStatus) {
      const steps = this.getTrackingSteps();
      const step = steps.find(s => s.status === stepStatus);
      return step ? step.current : false;
    },

    getStepCircleClass(stepStatus, isCurrent, isCompleted) {
      const isCancelled = stepStatus === 11 || stepStatus === 7;
      const isRTO = [8, 9, 10].includes(stepStatus);
      if (isCancelled) return 'exception-circle';
      if (isRTO) return 'rto-circle';
      if (isCurrent) return 'current-circle';
      if (isCompleted) return 'completed-circle';
      return 'pending-circle';
    },

    getTrackingStepDescription() {
      // This method is no longer used since we now display message directly from tracking records
      // Keeping for backward compatibility
      return '';
    },

    formatTrackingTime(timeString) {
      // Format like "09:23 AM, May 23,2025"
      if (!timeString) return '';
      try {
        const date = new Date(timeString);
        return date.toLocaleString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      } catch (e) {
        return timeString;
      }
    },

  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Multi-seller expanded row */
.expanded-row-content {
  background: rgba(103, 58, 183, 0.04);
}

.seller-sub-block {
  background: #fff;
  border-left: 3px solid #673ab7;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.action-btn-group {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}

.action-btn-group .action-btn-group__btn {
  border-radius: 0 !important;
  min-width: 40px !important;
  padding: 0 12px !important;
  margin: 0 !important;
}

.action-btn-group .action-btn-group__btn:not(:last-child) {
  border-right: 1px solid rgba(0, 0, 0, 0.1);
}

.items-mini-table >>> table {
  background: transparent;
}

.items-mini-table >>> th,
.items-mini-table >>> td {
  padding: 4px 8px !important;
  height: auto !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

.filter-field >>> .v-input__control {
  min-height: 48px;
}

.filter-field >>> .v-input__slot {
  font-size: 0.9rem;
}

/* Table Styling */
.orders-table >>> .v-data-table__wrapper {
  flex: 1;
}

.orders-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

.orders-table >>> .v-data-table-header th {
  font-weight: 600 !important;
  font-size: 0.85rem !important;
  color: var(--v-primary-base) !important;
  border-bottom: 2px solid var(--v-primary-base) !important;
}

.orders-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
}

.orders-table >>> tbody td {
  font-size: 0.85rem;
  padding: 8px 16px !important;
  border-bottom: 1px solid #e0e0e0 !important;
}

/* Pagination Input Styling */
.centered-input >>> .v-input__control >>> .v-input__slot >>> input {
  text-align: center;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.5rem;
  }

  .page-subtitle {
    font-size: 0.85rem;
  }

  .filter-row {
    flex-direction: column;
  }

  .filter-field >>> .v-input__control {
    min-height: 44px;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 1.25rem;
  }

  .orders-table >>> tbody td {
    padding: 6px 8px !important;
    font-size: 0.8rem;
  }

  .table-title {
    font-size: 1rem;
  }
}

/* Button and Chip Styling */
.v-btn {
  text-transform: none;
}

.v-chip {
  font-weight: 500;
}

/* Loading and Empty States */
.v-progress-circular {
  margin: 16px;
}

/* Delete Confirmation Dialog Styling */
.order-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.order-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Transition Effects */
.header-card,
.data-card,
.filter-field,
.v-btn,
.v-chip {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Tracking Progress Styles */
.tracking-container {
  padding: 20px 16px;
  background: #fafafa;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
}

/* ── Row layout ─────────────────────────────────────── */
.tracking-step {
  display: flex;
  align-items: flex-start;
  min-height: 64px;
  position: relative;
  transition: background 0.2s;
  border-radius: 8px;
  padding: 6px 8px;
}

.tracking-step.current {
  background: rgba(76, 175, 80, 0.06);
}
.tracking-step.exception {
  background: rgba(244, 67, 54, 0.05);
}
.tracking-step.refund {
  background: rgba(46, 125, 50, 0.06);
}
.tracking-step.rto {
  background: rgba(255, 152, 0, 0.05);
}

/* ── Step indicator column ──────────────────────────── */
.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 14px;
  flex-shrink: 0;
  position: relative;
}

/* ── Circles ────────────────────────────────────────── */
.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
}

/* Completed — solid green fill */
.completed-circle {
  background-color: #4CAF50;
  border: 2px solid #4CAF50;
  box-shadow: 0 2px 6px rgba(76, 175, 80, 0.35);
}

/* Current — pulsing green ring */
.current-circle {
  background-color: #4CAF50;
  border: 2px solid #43A047;
  box-shadow: 0 0 0 5px rgba(76, 175, 80, 0.18);
  animation: tracking-pulse 2s infinite;
}
.current-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fff;
  display: block;
}

/* Pending — outlined only, white fill */
.pending-circle {
  background-color: #ffffff;
  border: 2px dashed #BDBDBD;
  box-shadow: none;
}
.pending-number {
  font-size: 11px;
  font-weight: 700;
  color: #BDBDBD;
  line-height: 1;
}

/* Exception / RTO */
.exception-circle {
  background-color: #F44336;
  border: 2px solid #E53935;
  box-shadow: 0 2px 6px rgba(244, 67, 54, 0.35);
  animation: tracking-shake 0.5s ease-in-out;
}
.refund-circle {
  background-color: #2E7D32;
  border: 2px solid #1B5E20;
  box-shadow: 0 2px 6px rgba(46, 125, 50, 0.35);
}
.rto-circle {
  background-color: #FF9800;
  border: 2px solid #FB8C00;
  box-shadow: 0 2px 6px rgba(255, 152, 0, 0.35);
}

@keyframes tracking-pulse {
  0%   { box-shadow: 0 0 0 0   rgba(76, 175, 80, 0.45); }
  70%  { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);   }
  100% { box-shadow: 0 0 0 0   rgba(76, 175, 80, 0);    }
}
@keyframes tracking-shake {
  0%, 100% { transform: translateX(0); }
  25%       { transform: translateX(-3px); }
  75%       { transform: translateX(3px); }
}

/* ── Connecting lines ───────────────────────────────── */
.step-line {
  width: 2px;
  height: 36px;
  margin-top: 4px;
  position: absolute;
  top: 32px;
  left: 50%;
  transform: translateX(-50%);
  transition: background 0.3s ease;
  background-color: #E0E0E0;   /* default = pending */
}
.completed-line {
  background: linear-gradient(180deg, #4CAF50, #66BB6A);
}
.current-line {
  background: linear-gradient(180deg, #4CAF50 40%, #E0E0E0 100%);
}
.exception-line {
  background: linear-gradient(180deg, #F44336, #EF5350);
}
.rto-line {
  background: linear-gradient(180deg, #FF9800, #FFB74D);
}

/* ── Content area ───────────────────────────────────── */
.step-content {
  flex: 1;
  padding-top: 4px;
}

.step-title {
  font-size: 15px;
  font-weight: 600;
  color: #212121;
  margin-bottom: 3px;
  line-height: 1.3;
}
.active-title   { color: #2E7D32; font-weight: 700; }
.exception-title{ color: #C62828; font-weight: 700; }
.refund-title   { color: #2E7D32; font-weight: 700; }
.rto-title      { color: #E65100; font-weight: 700; }
.pending-title  { color: #9E9E9E; font-weight: 500; }

.step-time {
  font-size: 12px;
  color: #757575;
  margin-bottom: 3px;
  font-weight: 500;
}
.step-location {
  font-size: 12px;
  color: #9E9E9E;
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}
.step-description {
  font-size: 12px;
  color: #757575;
  font-style: italic;
  background: rgba(0,0,0,0.04);
  padding: 3px 8px;
  border-radius: 4px;
  margin-top: 4px;
}

/* Pending rows: dim the content slightly */
.tracking-step:not(.completed):not(.current):not(.exception):not(.rto) .step-content {
  opacity: 0.55;
}

.tracking-step:last-child .step-line { display: none; }

/* ── Responsive ─────────────────────────────────────── */
@media (max-width: 600px) {
  .tracking-container { padding: 14px 10px; }
  .step-circle  { width: 26px; height: 26px; }
  .step-title   { font-size: 13px; }
  .step-time    { font-size: 11px; }
  .step-location{ font-size: 10px; }
}

/* ── Nimbus tracking tab ────────────────────────────── */
.nimbus-panel { overflow: hidden; }
.nimbus-panel-header {
  padding: 10px 14px;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.2px;
}

.nimbus-stepper {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-top: 8px;
  position: relative;
}
.nimbus-step {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 0;
}
.nimbus-step-circle-wrap {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 36px;
}
.nimbus-step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  border: 2px solid #e0e0e0;
  transition: all 0.2s ease;
}
.nimbus-step-circle.completed {
  background: #fb8c00;
  border-color: #fb8c00;
}
.nimbus-step-circle.current {
  background: #fb8c00;
  border-color: #fb8c00;
  box-shadow: 0 0 0 4px rgba(251, 140, 0, 0.18);
}
.nimbus-step-line {
  position: absolute;
  top: 50%;
  right: 50%;
  width: 100%;
  height: 3px;
  background: #e0e0e0;
  transform: translateY(-50%);
  z-index: 0;
}
.nimbus-step-line.active { background: #fb8c00; }
.nimbus-step-label {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.25;
  color: rgba(0, 0, 0, 0.78);
  word-break: break-word;
}

.nimbus-timeline >>> .v-timeline-item__body {
  padding-bottom: 14px;
}

.nimbus-detail-grid > [class*="col-"] {
  padding: 8px 12px !important;
  border-right: 1px dashed rgba(0, 0, 0, 0.06);
  border-bottom: 1px dashed rgba(0, 0, 0, 0.06);
}
.nimbus-detail-label {
  color: rgba(0, 0, 0, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 10.5px;
  font-weight: 600;
  margin-bottom: 2px;
}
.nimbus-detail-value {
  color: rgba(0, 0, 0, 0.87);
  font-size: 13px;
  word-break: break-all;
}
@media (max-width: 600px) {
  .nimbus-detail-grid > [class*="col-"]:nth-child(2n) { border-right: none; }
}
@media (min-width: 600px) and (max-width: 960px) {
  .nimbus-detail-grid > [class*="col-"]:nth-child(3n) { border-right: none; }
}
@media (min-width: 960px) {
  .nimbus-detail-grid > [class*="col-"]:nth-child(4n) { border-right: none; }
}

.nimbus-event-card {
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  padding: 10px 12px;
  transition: all 0.18s ease;
}
.nimbus-event-card:hover {
  background: #f5f5f5;
  border-color: rgba(0, 0, 0, 0.12);
}
.nimbus-event-card.is-latest {
  background: #fff8f1;
  border-color: rgba(251, 140, 0, 0.35);
  box-shadow: 0 1px 3px rgba(251, 140, 0, 0.08);
}

@media (max-width: 600px) {
  .nimbus-step-circle { width: 26px; height: 26px; }
  .nimbus-step-label  { font-size: 11px; }
  .nimbus-step-circle-wrap { height: 30px; }
}
</style>